'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { v7 as uuidv7 } from 'uuid';
import {
  ConversationProvider,
  useConversation,
  useConversationClientTool,
} from '@elevenlabs/react';
import { Phone, MessageSquare, Mic, MicOff, Send, X, PhoneOff, ChevronDown, MessageCircle, Info } from 'lucide-react';
import { stripAudioDirectionTags } from '@/lib/strip-audio-tags';
import BookOneWave, { type BookOneWaveHandle } from '@/components/BookOneWave/BookOneWave';
import type { PaletteName } from '@/components/BookOneWave/palettes';
import {
  type SupportedLang,
  SUPPORTED_LANGS,
  getT,
  resolveInitialLang,
  writeStoredLang,
} from '@/lib/widget-i18n';
import { GroupLocationLinks, type GroupMemberOption } from './group-switcher';

// BookOneWave is statically imported — the file is only reachable from
// `'use client'` parents, so Next won't execute it on the server. The earlier
// `next/dynamic({ ssr: false })` wrapper introduced a loadable-ref timing race
// that crashed the third-party host install (the ref pointed at the loadable
// wrapper before the chunk resolved, so `setPalette` was missing on first
// render and threw, unmounting the whole widget). Static import resolves at
// module init so the ref always attaches to the real imperative handle.

// Three session modes — the user picks one at the start and it can't change mid-session.
// 'voice' creates a VoiceConversation (mic + TTS + text input as fallback).
// 'text'  creates a TextConversation (no mic prompt, no TTS, text I/O only).
// 'loud'  same as voice but mic STARTS muted — the caller uses a big
//         push-to-talk button to unmute only while they speak. Designed for
//         noisy venues (bars, restaurants) where ambient noise would
//         otherwise trigger the VAD or drown out the agent's TTS.
type WidgetMode = 'voice' | 'text' | 'loud';

export interface CateringWidgetTheme {
  logoUrl: string | null;
  primaryColor: string; // gold by default — used for buttons + accents
  accentColor: string; // darker gold — used for hover/gradient
  backgroundColor: string; // glass background
  borderColor: string; // glass border
  fontDisplay: string; // headings
  fontBody: string; // body
  textPrimary: string; // body copy (was hardcoded var(--t1))
  textSecondary: string; // supporting copy (was hardcoded var(--t3))
  surfacePanel: string; // transcript + text-input bg (was hardcoded burgundy)
  agentBubbleBg: string; // agent message bubble bg (was hardcoded burgundy)
  agentBubbleText: string; // agent message text color
  // User message bubble text color. Defaults to near-black which reads
  // well on the BookOne gold gradient but fails on brighter accent
  // colors (Ottavio's red). Bright-accent venues override this to white.
  userBubbleText?: string;
  // "No audio?" fallback overlay background. Defaults to a near-opaque
  // dark panel that fits BookOne's burgundy. Light-themed venues
  // (Ottavio's cream) override this so dark text isn't lost on a
  // dark mask.
  audioFallbackBg?: string;
  // Text color on the "No audio?" overlay. Defaults to theme.textPrimary
  // when the overlay is dark; light venues can override when the overlay
  // is light, so the transcript stays readable either way.
  audioFallbackText?: string;
  // BookOneWave palette for the voice-stage ribbon. Optional. Defaults to
  // 'akiroLight' (the Akiro-branded cream/bronze gradient) for backward
  // compatibility with the bespoke Akiro demo. BookOne-hosted surfaces
  // (/venue/[id], /catering/[id]) pass 'bookone' so the wave matches the
  // burgundy/gold card theme rather than bleeding Akiro cream into a
  // BookOne-branded context.
  wavePalette?: PaletteName;
  // Optional background for JUST the voice-stage wave frame (the rounded box
  // the ribbon lives in). Defaults to `surfacePanel`. Light-canvas venues
  // (Italian Kitchen's white panel) set this to a dark color so the brand
  // ribbon AND the white listening-state flip both read — a white-on-white
  // ribbon would be invisible on their normal panel. Affects only the wave
  // frame, not the rest of the widget chrome.
  waveStageBg?: string;
  // How to render the venue logo in the idle state.
  //  - 'circle' (default) — 120px circular crop with objectFit:cover. Right
  //    for square widget logos.
  //  - 'wide' — rectangular, max-height 56px with objectFit:contain. Right
  //    for wordmark logos where the full horizontal name must stay
  //    readable (e.g. Ottavio's red-O + black-script wordmark).
  logoDisplayMode?: 'circle' | 'wide';
  // Optional per-venue size for the 'wide' wordmark (defaults 56 / 220). A venue with a
  // bold horizontal mark (e.g. the Milos fish) can size it to the full header width without
  // affecting other wide-logo venues, which keep the defaults.
  logoMaxHeight?: number;
  logoMaxWidth?: number;
  // How the circular-crop logo fits within its 120x120 container. 'cover'
  // (default) fills the circle and crops anything outside — right for
  // square / nearly-square images like a bowl photo, dish thumbnail, or
  // venue avatar. 'contain' shrinks the image to fit fully inside, with
  // empty space around — right for wide wordmarks (like Akiro's AKIRO
  // kana lockup) where cropping would leave only the middle letters.
  logoObjectFit?: 'cover' | 'contain';
  // Disabled-state background for the chat send button. Defaults to BookOne
  // burgundy. Themed venues (Members Only monochrome, Ottavio cream) override
  // so the disabled button doesn't leak BookOne color into a foreign palette.
  sendButtonDisabledBg?: string;
  // Optional solid backplate behind the avatar logo (the 96x96 circle).
  // Defaults to transparent. Use when the venue's logo asset is light-on-
  // transparent (e.g. Matilda's cream M emblem) AND the widget panel bg is
  // also light, so a dark circle behind the logo keeps it readable. Doesn't
  // affect ANY logo asset color — only the container behind it.
  logoCircleBackground?: string;
}

// Grace period between `getUserMedia` resolving and `conversation.startSession`
// firing. Set to 0 — the cutoff is fixed by [pauses] audio tags prepended to
// the agent's FIRST_MESSAGE (TTS-side silence covers the iOS audio-sink
// takeoff time). The JS delay alone didn't help and made click-to-speak feel
// sluggish, so we removed it. Constant kept (rather than deleting the await
// entirely) so we can re-enable as a single-line tune if a future device
// shows a different timing race.
const FIRST_MESSAGE_AUDIO_GRACE_MS = 0;

const DEFAULT_THEME: CateringWidgetTheme = {
  logoUrl: null,
  primaryColor: '#F7B317',
  accentColor: '#C48F12',
  backgroundColor: 'rgba(51, 21, 21, 0.45)',
  borderColor: 'rgba(247, 179, 23, 0.18)',
  fontDisplay: 'var(--font-display, sans-serif)',
  fontBody: 'var(--font-body, sans-serif)',
  textPrimary: 'var(--t1, #f5f0eb)',
  textSecondary: 'var(--t3, #605850)',
  surfacePanel: 'rgba(26, 10, 10, 0.6)',
  agentBubbleBg: 'rgba(51, 21, 21, 0.7)',
  agentBubbleText: 'var(--t1, #f5f0eb)',
};

interface CateringWidgetProps {
  agentId: string;
  venueId: string;
  venueName: string;
  theme?: Partial<CateringWidgetTheme>;
  /**
   * Pre-generated UUID v7 passed to the agent as a dynamic variable at session
   * start. The agent also echoes it into a data_collection field, and the backend
   * uses it as the booking_request id. Lets the agent's `open_tracking_link`
   * client tool deep-link to /booking/{trackingId} even before the row lands.
   */
  trackingId?: string;
  /** Override the idle-state heading. Default: `Order Catering from {venueName}`. */
  titleOverride?: string;
  /** Override the idle-state sub-copy. */
  descriptionOverride?: string;
  /**
   * Override the idle-state "Chat" button label. When omitted, the button
   * falls back to the localized `t('widget.idle.chat')`. Set per-venue (via
   * BRAND_COPY[preset]?.chatLabel) to surface a venue-specific CTA like
   * "Chat with the Members Only Team". Undefined for every other venue.
   */
  chatLabelOverride?: string;
  /**
   * When true, skip the default 2px gold border around the circular logo — for
   * branded assets (like Akiro's hand-roll illustration) that already include a
   * ring in the image itself, so we don't double-ring it.
   */
  logoHasOwnBorder?: boolean;
  /**
   * Optional menu URL (e.g., venue.menus[0].fileUrl). When the agent fires the
   * `open_menu` client tool, the widget opens this URL in a new tab. If absent,
   * the tool registration is a no-op (agent shouldn't call it if no menu exists
   * in the venue config, but we fail gracefully).
   */
  menuUrl?: string;
  /**
   * When set, the widget renders a minimize (chevron-down) button as the first
   * control in the active-state header. Wrappers like FloatingWidget pass this
   * to collapse themselves. Leaving it undefined removes the button entirely —
   * intended for inline/full-page placements where there's nothing to collapse.
   */
  onMinimize?: () => void;
  /**
   * List of languages the caller can toggle between in IdleState. Defaults
   * to a single-language widget (no toggle rendered) when omitted. Pass
   * `['en', 'fr']` to expose a flag pill on the IdleState; the chosen lang
   * is passed to the ElevenLabs session via `overrides.language` +
   * `dynamicVariables.language`, and all widget chrome strings translate
   * to that language immediately.
   */
  supportedLanguages?: SupportedLang[];
  /**
   * Initial language. Defaults to browser detect → 'en'. When
   * supportedLanguages is omitted the widget still honours this override
   * (no toggle, but translations apply). When passed alongside
   * supportedLanguages, it seeds the toggle — user can still switch.
   */
  language?: SupportedLang;
  /**
   * Override the ambience audio URL. Defaults to '/audio/akiro-ambience.mp3'
   * which works when the widget runs at the BookOne origin (Next.js pages,
   * iframe embed). When the widget renders in a third-party host's DOM
   * via the custom-element bundle, the bundle entry point passes an
   * absolute URL pointing back to bookoneparty.com so the file resolves.
   */
  ambienceAudioUrl?: string;
  /**
   * Origin used to build absolute URLs for in-widget links — currently only
   * the post-call booking-tracking link from the open_tracking_link client
   * tool. Defaults to '' so Next.js call sites build same-origin relative
   * URLs ('/booking/<id>') unchanged. The custom-element bundle passes the
   * BookOne origin so '/booking/<id>' resolves to
   * 'https://www.bookoneparty.com/booking/<id>' instead of the host page's domain
   * (which would 404 — the host doesn't serve our app).
   */
  appOrigin?: string;
  /**
   * When true, render a location-pick screen BEFORE the call/chat idle. The
   * caller must select one of `locations` before any session starts. Used by
   * Boxcar Social (4 venues, single events team, single dashboard). Single-
   * location venues leave this falsy.
   */
  requireLocationPick?: boolean;
  /**
   * Locations to render in the picker. Each entry: { slug, name, neighborhood,
   * capacityHint }. The picker stacks them vertically (≥44pt tap targets) so
   * mobile + desktop scan-flow is consistent. Slug is what gets passed as
   * `location_preference` dynamic variable + recorded in extracted_data.
   */
  locations?: LocationOption[];
  /**
   * Per-location event guide URLs (Notion pages). When the agent fires the
   * `open_event_guide` client tool, the widget opens `eventGuideUrls[selectedLocation]`
   * in a new tab and pushes a link bubble into the transcript. Mirrors the
   * `menuUrl` + `open_menu` pattern.
   */
  eventGuideUrls?: Record<string, string>;
  /**
   * Opt-in flex-fill layout for the chat surface. When true, ActiveState +
   * EndedState wrap their content in a flex column where the transcript
   * fills available vertical space, and header + input row stay fixed at
   * top + bottom. Used by surfaces that have a known fullscreen container
   * (the book link `/book/<slug>`, the corner pill expanded on mobile)
   * so the chat behaves like iMessage / Slack — input pinned above the
   * keyboard, transcript scrolls internally.
   *
   * Default false → today's behavior: transcript has fixed
   * minHeight 240 / maxHeight 320, content is normal block flow. Required
   * for surfaces with unconstrained-height parents (the iframe widget,
   * venue/catering profiles, demo center variants) where flex-fill would
   * collapse the transcript to zero height.
   */
  fillContainer?: boolean;
  /**
   * Sibling venues in the same hospitality group. IdleState renders them as a
   * subtle "Interested in our other locations?" row of plain hyperlinks, each
   * navigating to the sibling's OWN hosted booking page (/book/<slug>) — URL,
   * branding, agent, and booking attribution all stay self-consistent. Pass
   * ONLY the siblings (current venue excluded); siblings without a slug are
   * skipped. Empty/undefined for standalone venues (nothing rendered).
   */
  groupMembers?: GroupMemberOption[];
  /**
   * Anchor target for the group links. '_blank' on embedded surfaces (the
   * /embed iframe and the light-DOM bundle on a venue's own site) so the
   * sibling's booking page opens as a full tab instead of inside the widget
   * frame. Defaults to '_self' for the standalone /book page.
   */
  groupLinkTarget?: '_self' | '_blank';
}

export interface LocationOption {
  slug: string;
  name: string;
  neighborhood?: string;
  capacityHint?: string;
}

export function CateringWidget(props: CateringWidgetProps) {
  return (
    <ConversationProvider>
      <CateringWidgetInner {...props} />
    </ConversationProvider>
  );
}

interface TranscriptMessage {
  role: 'agent' | 'user' | 'link';
  text: string;
  href?: string; // only populated on role='link' — the tappable URL
}

function CateringWidgetInner({
  // `agentId` is intentionally NOT destructured/used: the widget now connects
  // exclusively via a branch-correct signed URL (see fetchSignedUrl). It stays
  // on CateringWidgetProps for backward compat — the custom-element bundle and
  // wrapper components still pass it — but a bare-agentId connection is never
  // started (it would route bookings to the wrong environment's webhook).
  venueId: initialVenueId,
  venueName: initialVenueName,
  theme: themeOverride,
  trackingId,
  titleOverride,
  descriptionOverride,
  chatLabelOverride,
  logoHasOwnBorder,
  menuUrl,
  onMinimize,
  supportedLanguages,
  language: languageOverride,
  ambienceAudioUrl = '/audio/akiro-ambience.mp3',
  appOrigin = '',
  requireLocationPick = false,
  locations,
  eventGuideUrls,
  fillContainer = false,
  groupMembers,
  groupLinkTarget = '_self',
}: CateringWidgetProps) {
  const theme: CateringWidgetTheme = { ...DEFAULT_THEME, ...themeOverride };
  // The widget is permanently bound to the mounted venue — the booking
  // connection (fetchSignedUrl), branding, and attribution never re-point.
  // Group siblings render as plain links to their OWN /book/<slug> pages
  // (groupLinkItems below). The earlier in-place venue swap let a customer
  // book a sibling venue under this venue's URL + branding — removed.
  const venueId = initialVenueId;
  const venueName = initialVenueName;
  // Linkable sibling pages, name-sorted for stable order. Siblings without a
  // slug have no hosted page → skipped. appOrigin prefixes the href so the
  // light-DOM bundle (mounted on a venue's own site) resolves to the
  // canonical host; on /book and /embed it's '' → same-origin relative.
  const groupLinkItems = useMemo(
    () =>
      (groupMembers ?? [])
        .filter((m) => !!m.slug && m.venueId !== initialVenueId)
        .map((m) => ({ name: m.name, href: `${appOrigin}/book/${m.slug}` }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [groupMembers, appOrigin, initialVenueId],
  );
  // Language state — seeded from: explicit prop → localStorage → browser
  // detect → DEFAULT_LANG. Single state value used in three places: (1)
  // translate all widget chrome, (2) forward to ElevenLabs at startSession
  // as `overrides.language` + `dynamicVariables.language`, (3) render the
  // flag toggle on IdleState when supportedLanguages is set.
  //
  // We resolve once on mount. After that, changes come from the IdleState
  // toggle. Changing lang mid-session would require a restart (ElevenLabs
  // bakes language at session open), so we only expose the toggle in idle.
  const [language, setLanguage] = useState<SupportedLang>(() =>
    // SSR-safe — resolveInitialLang handles missing window/navigator.
    // Pass supportedLanguages so a stale localStorage choice from a
    // bilingual venue can't leak into a single-language venue.
    resolveInitialLang(languageOverride, supportedLanguages),
  );
  // If the parent passes an explicit language prop after mount (e.g., in
  // response to a URL param change), honour it — but only if it's in the
  // venue's supportedLanguages.
  useEffect(() => {
    if (
      languageOverride &&
      SUPPORTED_LANGS.includes(languageOverride) &&
      (!supportedLanguages || supportedLanguages.includes(languageOverride))
    ) {
      setLanguage(languageOverride);
    }
  }, [languageOverride, supportedLanguages]);
  const t = getT(language);
  // Client-tool handlers push a "link bubble" into the transcript — a third
  // transcript role ('link') that renders as a tappable gold pill inline with
  // the conversation. This is more reliable than window.open, which gets
  // swallowed by iOS/Android popup-blockers when called from a WebSocket
  // callback (no user-gesture). A tap on the inline link IS a user-gesture,
  // so it always opens. We still attempt window.open best-effort for desktop.
  //
  // *Fired state is kept for idempotency: if the agent calls the tool twice
  // (re-share after a clarifying question), we don't want to push a duplicate
  // bubble. Reset on mode switch — a new session gets a fresh slate.

  // Voice-repair banner state. Set when we detect either:
  //  - user is in text mode AND agent says "still there" (ElevenLabs silence prompt)
  //  - user typed "can't hear" / "no audio" / "turn on voice" / "mic" / similar
  // Renders a gold "🎤 Switch back to voice" banner above the text input. Clears on
  // mode switch so it doesn't linger across sessions.
  const [needsVoiceRepair, setNeedsVoiceRepair] = useState(false);
  const [trackingLinkFired, setTrackingLinkFired] = useState(false);
  useConversationClientTool('open_tracking_link', () => {
    if (trackingLinkFired) return; // idempotent — tool already fired this session
    const tid = trackingIdRef.current;
    if (!tid) {
      console.warn('catering-widget: open_tracking_link called but no tracking_id ref');
      return;
    }
    // Absolute URL when running in the custom-element bundle on a foreign
    // host (appOrigin = 'https://www.bookoneparty.com'); same-origin relative when
    // running inside Next.js pages on bookoneparty.com itself (appOrigin = '').
    // Both window.open at line 248 AND the LinkPill <a href> at line 252+
    // inherit this href, so fixing the one construction fixes both call
    // sites.
    const href = `${appOrigin}/booking/${tid}`;
    // Best-effort desktop auto-open. Blocked silently on iOS/Android.
    try {
      window.open(href, '_blank', 'noopener,noreferrer');
    } catch {
      // ignore — the inline link below is the reliable path
    }
    setTranscript((prev) => [...prev, { role: 'link', text: 'Tap to open your booking →', href }]);
    setTrackingLinkFired(true);
    // Phase 9.A (2026-05-07): chat sessions don't have a natural "end signal"
    // like voice (no Twilio hangup, no mic close). ElevenLabs holds the
    // session in_progress for ~5 min until inactivity timeout fires, which
    // blocks BookOne's post-call processing and leaves /booking/[id] stuck
    // on "Preparing your booking…" for the user. Explicitly end the session
    // after a 4-second grace so the agent's closing message can render
    // naturally. Voice mode is left alone — its WebRTC connection ends when
    // the audio stream closes (no premature TTS cut).
    if (mode === 'text') {
      setTimeout(() => {
        try {
          console.log('[bookone-widget] auto-endSession after open_tracking_link');
          conversation.endSession();
        } catch (err) {
          console.warn('[bookone-widget] endSession failed (likely already closed):', err);
        }
      }, 4000);
    }
  });

  // open_inquiry_link — the inquiry-path twin of open_tracking_link. Members
  // Only's dual-intake agent fires THIS (not open_tracking_link) when it wraps
  // up a general inquiry, so the in-widget pill points at /inquiry/{id}. The
  // backend saves the inquiry under the same browser tracking_id, so the pill,
  // the confirmation email, and the SMS all resolve to one row. Mirrors the
  // event pill exactly — idempotency, desktop auto-open, text-mode auto-end —
  // only the path + bubble copy differ.
  const [inquiryLinkFired, setInquiryLinkFired] = useState(false);
  useConversationClientTool('open_inquiry_link', () => {
    if (inquiryLinkFired) return; // idempotent — tool already fired this session
    const tid = trackingIdRef.current;
    if (!tid) {
      console.warn('catering-widget: open_inquiry_link called but no tracking_id ref');
      return;
    }
    const href = `${appOrigin}/inquiry/${tid}`;
    try {
      window.open(href, '_blank', 'noopener,noreferrer');
    } catch {
      // ignore — the inline link below is the reliable path
    }
    setTranscript((prev) => [...prev, { role: 'link', text: 'Tap to open your inquiry →', href }]);
    setInquiryLinkFired(true);
    if (mode === 'text') {
      setTimeout(() => {
        try {
          console.log('[bookone-widget] auto-endSession after open_inquiry_link');
          conversation.endSession();
        } catch (err) {
          console.warn('[bookone-widget] endSession failed (likely already closed):', err);
        }
      }, 4000);
    }
  });

  // open_menu — agent fires this when the caller asks about food/drinks and
  // the agent wants to show rather than describe. Same inline-link pattern.
  const [menuLinkFired, setMenuLinkFired] = useState(false);
  useConversationClientTool('open_menu', () => {
    if (menuLinkFired) return; // idempotent
    if (!menuUrl) {
      console.warn('catering-widget: open_menu called but no menuUrl prop');
      return;
    }
    try {
      window.open(menuUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // ignore — inline link is the reliable path
    }
    setTranscript((prev) => [...prev, { role: 'link', text: 'Tap to view the menu →', href: menuUrl }]);
    setMenuLinkFired(true);
  });

  // Location-pick state — pre-idle screen for multi-location venues (Boxcar).
  // Persists for the session: customer picks once, then the widget skips the
  // picker on subsequent re-opens. To switch locations, end the session and
  // re-open. selectedLocation is the slug; the matching LocationOption is
  // resolved on use. Single-location venues never read this.
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const selectedLocationOption = locations?.find((l) => l.slug === selectedLocation) ?? null;

  // open_event_guide — agent fires this when the caller asks about menu /
  // food / packages / details and the venue has a per-location guide URL
  // (Boxcar Social). Mirrors open_menu exactly: link bubble in transcript +
  // best-effort window.open. Idempotent on a per-session flag.
  const [eventGuideLinkFired, setEventGuideLinkFired] = useState(false);
  useConversationClientTool('open_event_guide', () => {
    if (eventGuideLinkFired) return;
    const url = selectedLocation && eventGuideUrls?.[selectedLocation];
    if (!url) {
      console.warn('catering-widget: open_event_guide called but no eventGuideUrls entry for', selectedLocation);
      return;
    }
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // ignore — inline link is the reliable path
    }
    setTranscript((prev) => [...prev, { role: 'link', text: 'Tap to view the event guide →', href: url }]);
    setEventGuideLinkFired(true);
  });

  // request_live_agent — agent fires this when the caller asks to speak with a
  // human. Backend orchestrates a Twilio Conference between the customer's
  // phone and the venue's manager phone. The widget's job is to:
  //   1. Validate the phone (defence-in-depth — agent already did)
  //   2. POST to /venues/widget/request-live-agent with the captured fields
  //   3. Render a "your phone will ring shortly" banner so the caller knows
  //      to expect the inbound call.
  // Idempotent: server-side dedup catches double-fires within 60s, but we
  // also short-circuit here so the banner doesn't double-render.
  const [liveTransferState, setLiveTransferState] = useState<
    | { stage: 'idle' }
    | { stage: 'connecting'; transferId?: string }
    | { stage: 'ringing'; transferId: string }
    | { stage: 'done'; transferId: string; outcome: string }
    | { stage: 'failed'; reason: string }
  >({ stage: 'idle' });
  // Cancellation flag for the live-transfer poll loop. The poll chain runs
  // setTimeout(poll, 2000) for up to 60s; if the widget unmounts mid-flow
  // (caller closes the panel, mode change, etc.) we'd otherwise still be
  // setting state on a stale closure → React warns + does nothing.
  const liveTransferCancelledRef = useRef<boolean>(false);
  useEffect(() => {
    return () => {
      liveTransferCancelledRef.current = true;
    };
  }, []);
  useConversationClientTool('request_live_agent', async (params: unknown) => {
    // Block ONLY when a transfer is currently in flight. Allow fresh fires
    // from terminal states (`done` / `failed`) so the customer can retry
    // after a missed connection or initiate a second transfer in the same
    // session — the agent might pick up a "let's try again" intent. Server
    // dedupes by conversation+in-flight, so a fresh fire after a terminal
    // outcome correctly creates a new row + new Twilio calls.
    if (liveTransferState.stage === 'connecting' || liveTransferState.stage === 'ringing') {
      setTranscript((prev) => [
        ...prev,
        { role: 'agent', text: "I'm already connecting you with the team — give me a moment." },
      ]);
      return;
    }
    // Reset cancellation flag — the previous attempt's poll loop may have
    // tripped this on its own teardown; we're still mounted and want the
    // new poll loop to run.
    liveTransferCancelledRef.current = false;
    // Tool params are snake_case (ElevenLabs convention). The intake fields are
    // best-effort: the agent fills whatever it has captured so far, blanks the
    // rest. Everything except customer_phone is optional.
    const p = (params ?? {}) as {
      customer_phone?: string;
      customer_name?: string;
      customer_email?: string;
      event_type?: string;
      preferred_dates?: string;
      guest_count?: string | number;
      budget?: string;
    };
    const phone = (p.customer_phone ?? '').trim();
    if (!phone || !/^\+[1-9][0-9]{6,14}$/.test(phone)) {
      console.warn('request_live_agent: missing/invalid customer_phone');
      setTranscript((prev) => [
        ...prev,
        { role: 'agent', text: "I couldn't dial that number — try again with country code, like +1 212 555 1234." },
      ]);
      return;
    }
    // Build capturedIntake using snake_case keys — matches the tool param
    // shape, the phone-path's data_collection_results, and what the dashboard
    // + summarizer both read. Single source of truth across paths. Only
    // include fields the agent actually populated so the summarizer doesn't
    // render empty bullets.
    const capturedIntake: Record<string, unknown> = {};
    if (p.event_type) capturedIntake.event_type = p.event_type;
    if (p.preferred_dates) capturedIntake.preferred_dates = p.preferred_dates;
    if (p.guest_count !== undefined && p.guest_count !== '') capturedIntake.guest_count = p.guest_count;
    if (p.budget) capturedIntake.budget = p.budget;
    setLiveTransferState({ stage: 'connecting' });
    setTranscript((prev) => [
      ...prev,
      { role: 'agent', text: `Connecting you with the team — your phone will ring at ${phone} in a few seconds. Please answer to be connected.` },
    ]);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/venues/widget/request-live-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venueId,
          conversationId: trackingIdRef.current,
          customerPhone: phone,
          customerName: p.customer_name ?? null,
          customerEmail: p.customer_email ?? null,
          capturedIntake,
        }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        console.error(`request_live_agent backend failed: ${res.status} ${detail}`);
        setLiveTransferState({ stage: 'failed', reason: 'backend_error' });
        return;
      }
      const data = (await res.json()) as { status: string; transferId?: string; outcome?: string };
      if (data.status === 'feature_disabled') {
        setLiveTransferState({ stage: 'failed', reason: 'feature_disabled' });
        setTranscript((prev) => [
          ...prev,
          { role: 'agent', text: "Right now I'd recommend reaching out by email — the team will follow up there." },
        ]);
        return;
      }
      if (data.status === 'rate_limited') {
        setLiveTransferState({ stage: 'failed', reason: 'rate_limited' });
        return;
      }
      if (data.transferId) {
        setLiveTransferState({ stage: 'ringing', transferId: data.transferId });
        // Poll for outcome every 2s for up to 60s. Aborts cleanly on unmount
        // via liveTransferCancelledRef.
        const start = Date.now();
        const poll = async (): Promise<void> => {
          if (liveTransferCancelledRef.current) return;
          if (Date.now() - start > 60_000) {
            if (liveTransferCancelledRef.current) return;
            setLiveTransferState((cur) =>
              cur.stage === 'ringing' ? { stage: 'done', transferId: cur.transferId, outcome: 'pending' } : cur,
            );
            return;
          }
          try {
            const sres = await fetch(`${apiUrl}/venues/widget/live-transfer-status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ transferId: data.transferId }),
            });
            if (liveTransferCancelledRef.current) return;
            if (sres.ok) {
              const sdata = (await sres.json()) as { outcome: string };
              if (sdata.outcome && sdata.outcome !== 'pending') {
                setLiveTransferState({ stage: 'done', transferId: data.transferId!, outcome: sdata.outcome });
                return;
              }
            }
          } catch {
            // poll error — keep trying
          }
          if (!liveTransferCancelledRef.current) {
            setTimeout(poll, 2_000);
          }
        };
        setTimeout(poll, 2_000);
      }
    } catch (err) {
      console.error('request_live_agent failed:', err);
      setLiveTransferState({ stage: 'failed', reason: 'network_error' });
    }
  });
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [textInput, setTextInput] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Which session mode is currently active. Set when the user clicks Call or Chat;
  // cleared on disconnect. The active-state UI reads this to decide whether to show
  // the mic mute button and whether to render "Speaking..." / "Listening..." vs "Typing...".
  const [mode, setMode] = useState<WidgetMode | null>(null);
  // True after a conversation fully ends (not during a mode switch). While true,
  // the widget renders an "ended" state that preserves the transcript + the
  // booking-link pill so the user can still tap/re-read what was said. Reset
  // when the user starts a new call/chat from the ended state.
  const [hasEnded, setHasEnded] = useState(false);
  // Voice-mode ephemeral echo — when the caller types a message while the
  // wave is showing, we flash their text as a fading bubble over the wave so
  // they can verify what they typed (the transcript is hidden in voice mode).
  const [typedEcho, setTypedEcho] = useState<{ text: string; id: string } | null>(null);
  // "No audio?" fallback overlay. When toggled on (via the header pill), the
  // voice stage overlays the running transcript + a hint "Can't hear? Type
  // below" so the caller can keep the conversation going by typing without
  // losing the voice session context. Off by default; tapping the pill
  // toggles. Distinct from the auto-triggered `audioDroppedOut` banner (which
  // surfaces only when the SDK's audio-frame counter stalls).
  const [showAudioFallback, setShowAudioFallback] = useState(false);
  // One-time hint shown when the caller enters Loud Room. Explains why the
  // OS mic indicator stays lit even though the agent hears nothing (soft
  // mute at the AudioWorklet layer — browser spec keeps the indicator on
  // until track.stop() releases the hardware, which we don't do because it
  // would require a session restart on unmute). Dismissible or auto-hides
  // after 6s. Session-scoped — fresh call resets it.
  const [showLoudRoomHint, setShowLoudRoomHint] = useState(false);
  // True when the audio-dropout watchdog detects a stall in TTS frames while
  // the agent is mid-response. Drives the "Audio cut out — tap to reconnect"
  // inline pill in ActiveState. Reset whenever isSpeaking toggles off (agent
  // finished naturally) or reconnection succeeds (next onConnect clears the
  // counter, watchdog sees movement, effect pulls this back to false).
  const [audioDroppedOut, setAudioDroppedOut] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  // Captures the current conversation ID so the tab-close handler can notify the API
  // even after useConversation's state has been cleared.
  const conversationIdRef = useRef<string | null>(null);
  // Audio-frame telemetry: count onAudio callbacks to detect mid-conversation playback dropout.
  // No fix available (SDK doesn't expose the output AudioContext), but the count helps us
  // prove/disprove the dropout hypothesis on the next test.
  const audioFrameCountRef = useRef(0);
  // Mid-session mode switch state. The SDK can't flip a TextConversation into a
  // VoiceConversation — they're separate classes. So "switching modes" means:
  // 1. Suppress the onDisconnect → notifyConversationEnded path for the OLD session
  //    (otherwise we'd create a booking_request from the intermediate session)
  // 2. Start a fresh session in the target mode
  // 3. Pass the previous transcript as a contextualUpdate so the agent has the
  //    history and doesn't re-ask for fields the user already gave
  const switchingModeRef = useRef(false);
  const pendingModeRef = useRef<WidgetMode | null>(null);
  const pendingContextRef = useRef<string | null>(null);
  // Session-scoped tracking_id. Seeded from the `trackingId` prop for the first
  // conversation, then regenerated at the top of each user-initiated start
  // (startCall / startLoudRoom / startChat). Held steady across voice↔text mode
  // switches so the transcript-continuity contextualUpdate lands on the same
  // booking row. The parent page's useState(() => uuidv7()) becomes a one-shot
  // seed after this ref takes over. Previously the prop leaked across
  // consecutive conversations in the same page mount — the second session's
  // beacon matched the first session's booking_request and the backend returned
  // `reason: 'duplicate'`, dropping the new intake + its confirmation email.
  const trackingIdRef = useRef<string>(trackingId ?? uuidv7());

  const apiBase = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001';

  const notifyConversationEnded = useCallback(
    (cid: string) => {
      // Include the session-scoped tracking_id so the backend can use the SAME
      // UUID the browser opened in the tracking tab — bypasses the unreliable
      // agent-echoed data_collection field (LLM sometimes strips dashes /
      // hallucinates). Read from the ref (not the prop) so second + later
      // conversations in the same page mount get a unique tracking_id — the
      // prop is stable per page, which previously caused the second session's
      // beacon to match the first session's booking_request.
      // language is forwarded so the backend can send the confirmation email
      // in the caller's selected language (FR / EN).
      const trackingId = trackingIdRef.current;
      const body = JSON.stringify({ conversationId: cid, venueId, trackingId, language });
      const url = `${apiBase}/venues/widget/conversation-ended`;
      // sendBeacon is the correct primitive for "tell the server this happened" during
      // page unload — browsers guarantee delivery even after the page is gone.
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
          return;
        } catch {
          // fall through to fetch
        }
      }
      // Fallback: fetch with keepalive, which also survives page unload.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch((err) => {
        console.warn('catering-widget: failed to notify conversation-ended', err);
      });
    },
    [apiBase, venueId, language],
  );

  const conversation = useConversation({
    // Dropout diagnostics. User reported repeat "ElevenLabs audio cuts out
    // even though ambience keeps the browser channel alive" — strongly
    // suggests server-side disconnects the SDK isn't flagging loudly.
    // Surface everything to the console with a stable prefix so callers can
    // paste a timeline for triage.
    //
    // DisconnectionDetails reasons (from @elevenlabs/types):
    //   - 'user'  : we called endSession() locally
    //   - 'agent' : ElevenLabs server closed the socket (this is the one
    //               the user is hitting — agent disconnects mid-session,
    //               likely LLM error, rate-limit, or workflow terminal)
    //   - 'error' : transport error (network, WebSocket close)
    // Dropout diagnostics — status changes log to console. The existing
    // onError below also logs; the existing onDisconnect below now logs
    // reason + closeCode + closeReason.
    onStatusChange: ({ status }) => {
      console.log('[akiro-widget] status →', status);
    },
    onMessage: ({ message, source }) => {
      const stripped = stripAudioDirectionTags(message);
      if (!stripped) return; // skip empty messages (pure direction tags only)
      // Agent messages get accent normalization; user messages always render verbatim.
      setTranscript((prev) => [...prev, { role: source === 'ai' ? 'agent' : 'user', text: stripped }]);

      // Voice-repair detection. If the caller is stuck in text mode because ElevenLabs
      // auto-switched them away from voice after silence, the agent may prompt "are
      // you still there?" — that's our cue to surface the "Switch back to voice"
      // banner. Also catch explicit user signals: "can't hear", "no audio", "turn
      // voice back on", "mic isn't working" — same intent.
      const lower = stripped.toLowerCase();
      if (source === 'ai' && /\bstill there\b|\bhear me\b/.test(lower)) {
        setNeedsVoiceRepair(true);
      } else if (source !== 'ai' && /can'?t hear|no audio|turn (on|back) voice|mic (?:isn'?t|not) working|voice back/.test(lower)) {
        setNeedsVoiceRepair(true);
      }
    },
    onError: (err, context) => {
      // User-visible fallback + diagnostic log for triage.
      console.warn('[akiro-widget] SDK error:', err, context);
      setErrorMsg(typeof err === 'string' ? err : 'Connection error. Please try again.');
    },
    onConnect: ({ conversationId }) => {
      conversationIdRef.current = conversationId;
      audioFrameCountRef.current = 0;
      // If we were switching modes, the previous session's transcript is waiting
      // to be replayed as context so the agent can resume without re-asking.
      // sendContextualUpdate injects text into the agent's awareness without
      // making the user take a turn.
      if (pendingContextRef.current) {
        try {
          conversation.sendContextualUpdate(pendingContextRef.current);
        } catch (err) {
          console.warn('catering-widget: failed to send contextual update on mode switch', err);
        }
        pendingContextRef.current = null;
      }
    },
    onAudio: () => {
      audioFrameCountRef.current += 1;
    },
    onDisconnect: (details) => {
      const cid = conversationIdRef.current;
      // Diagnostic log — paste this into triage when audio cuts out. The
      // `reason` field tells us who closed: 'user' (we hit endSession),
      // 'agent' (ElevenLabs server-side closed — likely LLM error,
      // rate-limit, or workflow terminal), or 'error' (transport).
      // closeCode + closeReason are WebSocket close frame data when
      // available. This is the fastest path to knowing if dropouts are
      // OUR bug (needs fix) or ElevenLabs-side (open an upstream issue).
      console.warn(
        '[akiro-widget] disconnected — conv_id:', cid,
        'reason:', details?.reason,
        'closeCode:', details && 'closeCode' in details ? details.closeCode : undefined,
        'closeReason:', details && 'closeReason' in details ? details.closeReason : undefined,
        'message:', details && 'message' in details ? details.message : undefined,
      );
      conversationIdRef.current = null;
      setTextInput('');

      // Mode switch path: skip the booking-request creation, start a new session
      // in the target mode instead. Only the FINAL session (when the user clicks
      // End or closes the tab) will create a booking_request.
      if (switchingModeRef.current && pendingModeRef.current) {
        const targetMode = pendingModeRef.current;
        switchingModeRef.current = false;
        pendingModeRef.current = null;
        setMode(targetMode);
        setNeedsVoiceRepair(false);

        (async () => {
          // Pre-request mic permission BEFORE fetching the signed URL when
          // entering voice mode — same Round 5 cutoff fix as startCall. The
          // fetchSignedUrl network call below becomes the natural audio-sink
          // settling window between mic-grant and startSession.
          let micGranted = false;
          if (targetMode === 'voice') {
            primeAudioContext();
            try {
              await navigator.mediaDevices.getUserMedia({ audio: true });
              micGranted = true;
            } catch {
              setErrorMsg('Mic blocked — staying in chat mode. Allow mic and try again.');
              setMode('text');
            }
          }

          const signedUrl = await fetchSignedUrl(apiBase, venueId);
          if (!signedUrl) {
            // Could not obtain a branch-correct signed URL — fail loud rather
            // than reconnecting on the Main branch (wrong-environment webhook).
            setErrorMsg('Connection error. Please try again.');
            setMode(null);
            return;
          }
          const sessionConfig = { signedUrl, connectionType: 'websocket' as const };

          await new Promise<void>((resolve) => setTimeout(resolve, FIRST_MESSAGE_AUDIO_GRACE_MS));

          if (targetMode === 'voice' && micGranted) {
            conversation.startSession({
              ...sessionConfig,
              dynamicVariables: buildDynamicVariables(),
              overrides: sessionLanguageOverride,
            });
          } else {
            // Text session — either targetMode='text' from the start, or
            // voice mode where mic was blocked above (we already flipped
            // setMode('text') and surfaced the error).
            conversation.startSession({
              ...sessionConfig,
              textOnly: true,
              dynamicVariables: buildDynamicVariables(),
              overrides: { ...sessionLanguageOverride, conversation: { textOnly: true } },
            });
          }
        })();
        return;
      }

      // Final disconnect — create the booking_request from this session.
      // Flip to the "ended" state so the transcript + booking-link pill remain
      // on screen. The user can still tap the link, re-read the conversation,
      // or start a new chat. Don't clear mode/transcript here — EndedState
      // reads both for rendering.
      if (cid) {
        notifyConversationEnded(cid);
      }
      setHasEnded(true);
    },
  });

  // Hoisted above the audio-lifecycle effects below so they can depend on it
  // (MediaSession playbackState sync, dropout watchdog). Same `status`/`isActive`
  // derivations are also used later by the ActiveState render.
  const status = conversation.status;
  const isActive = status === 'connected' || status === 'connecting';
  const isSpeaking = conversation.isSpeaking;

  // Tab-close fallback: if the user closes the tab mid-conversation, onDisconnect may
  // never fire because the page is already gone. beforeunload/pagehide listeners catch
  // this case; sendBeacon inside them guarantees the POST goes through. The API side is
  // idempotent (dedupes on elevenlabs_conversation_id) so any accidental double-POST is safe.
  useEffect(() => {
    const handleUnload = () => {
      const cid = conversationIdRef.current;
      if (cid) notifyConversationEnded(cid);
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [venueId, notifyConversationEnded]);

  // Visibility-change re-prime. When the caller returns from the lock screen
  // or tab-switching, the OS may have suspended our cached AudioContext. Call
  // primeAudioContext() to re-resume it so the SDK's next audio frame lands
  // on a ready output graph. iOS Safari in particular aggressively suspends
  // backgrounded contexts; this is the simplest resumption path that doesn't
  // require reaching into the SDK's internal AudioContext (which isn't
  // exposed — investigated 2026-04-14).
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden && mode === 'voice' && conversationIdRef.current) {
        primeAudioContext();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [mode]);

  // MediaSession — registers the tab as an audio source so iOS/Android treat
  // this as a call-style session. Enables hardware lock-screen / Dynamic
  // Island / Bluetooth-button controls (pause/play/stop). Gated on voice mode
  // + API availability. No actual audio plays — metadata-only registration.
  //
  // Action mapping: pause = mute, play = unmute, stop = end the session.
  // playbackState stays in sync with conversation.isMuted via the effect
  // below so the lock-screen control reflects reality.
  useEffect(() => {
    if (mode !== 'voice') return;
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    try {
      ms.metadata = new MediaMetadata({
        title: `${venueName} — Private Events`,
        artist: 'BookOne',
      });
      ms.setActionHandler('pause', () => conversation.setMuted(true));
      ms.setActionHandler('play', () => conversation.setMuted(false));
      ms.setActionHandler('stop', () => conversation.endSession());
      ms.playbackState = 'playing';
    } catch (err) {
      console.warn('catering-widget: MediaSession setup failed', err);
    }
    return () => {
      try {
        ms.metadata = null;
        ms.setActionHandler('pause', null);
        ms.setActionHandler('play', null);
        ms.setActionHandler('stop', null);
        ms.playbackState = 'none';
      } catch {
        /* ignore cleanup errors */
      }
    };
  }, [mode, venueName, conversation]);

  // Keep MediaSession playbackState in sync with the SDK's mute state so the
  // lock-screen control toggles from paused → playing when the caller unmutes.
  useEffect(() => {
    if (mode !== 'voice') return;
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = conversation.isMuted ? 'paused' : 'playing';
  }, [conversation.isMuted, mode]);

  // Audio-dropout watchdog. `audioFrameCountRef` is incremented inside onAudio
  // (the SDK's per-frame TTS callback). While the agent is speaking, the
  // counter should advance within ~a second. If 2.5s pass and the counter
  // hasn't moved, the caller's audio output has stalled — likely an iOS
  // Safari backgrounded-context suspension that visibility re-prime couldn't
  // recover, or a Bluetooth-swap mid-response. Surface a reconnect pill in
  // ActiveState; the handler reuses handleSwitchMode('voice'), which already
  // does "save transcript → endSession → restart with contextual update" —
  // zero new reconnect logic.
  //
  // Resets when isSpeaking flips to false (agent finished its turn, not a
  // dropout).
  useEffect(() => {
    if (!isSpeaking || mode !== 'voice') {
      if (audioDroppedOut) setAudioDroppedOut(false);
      return;
    }
    const startCount = audioFrameCountRef.current;
    const t = setTimeout(() => {
      if (audioFrameCountRef.current === startCount) {
        setAudioDroppedOut(true);
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [isSpeaking, mode, audioDroppedOut]);

  // Sticky-bottom auto-scroll. We follow new messages only while the user is
  // parked at the bottom — but that "is the user at the bottom?" decision is
  // made from their LAST manual scroll (tracked in `stickToBottomRef` via the
  // onScroll handler), NOT re-measured after a message is appended.
  //
  // The old approach measured `distanceFromBottom` AFTER the new message was in
  // the DOM and only scrolled when it was < 100px. That broke on mobile: a
  // freshly-appended message wraps tall on a narrow viewport, so the
  // post-append distance equals roughly the new message's height — frequently
  // > 100px — and the guard wrongly decided the user had scrolled away and
  // stopped following. Deciding from the prior scroll position avoids this.
  //
  // The scroll write is deferred to requestAnimationFrame so it runs after the
  // new bubble has laid out; a synchronous `scrollTop = scrollHeight` reads a
  // stale (too-short) scrollHeight and lands short of the bottom in flex-fill
  // (`fillContainer`) mode on iOS Safari.
  const stickToBottomRef = useRef(true);
  const handleTranscriptScroll = useCallback<React.UIEventHandler<HTMLDivElement>>((e) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el || !stickToBottomRef.current) return;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [transcript]);

  // Dynamic variables forwarded into the agent session + echoed back in the
  // post-call webhook payload. tracking_id lets the agent deep-link
  // /booking/{id}. language lets bilingual agents (Ottavio) open in the
  // caller's picked language — the SDK's `overrides.language` (set inside
  // startSession below) is the primary language switch; exposing it as a
  // dynamic variable here lets the system prompt template reference it.
  //
  // Built as a function (not a const) so each startSession call reads the
  // CURRENT `trackingIdRef` value. User-initiated starts (startCall /
  // startLoudRoom / startChat) bump the ref before calling startSession,
  // producing a fresh tracking_id per conversation. Mode-switch restarts
  // (inside onDisconnect) DON'T bump the ref, so transcript continuity
  // across voice↔text within a single conversation is preserved.
  const buildDynamicVariables = (): Record<string, string | number | boolean> => {
    const base: Record<string, string | number | boolean> = {
      tracking_id: trackingIdRef.current,
      venue_id: venueId,
      language,
    };
    // Multi-location venues pass the picked location at session start so the
    // agent's first_message can interpolate {{location_name}} and the agent
    // skips the "which location?" question. Falls through to base for
    // single-location venues.
    if (selectedLocation && selectedLocationOption) {
      base.location_preference = selectedLocation;
      base.location_name = selectedLocationOption.name;
      const guideUrl = eventGuideUrls?.[selectedLocation] ?? '';
      base.event_guide_url = guideUrl;
    }
    return base;
  };
  // Session-open overrides. `agent.language` picks which language_preset
  // (if any) the agent loads — e.g., Ottavio's `language_presets.en`
  // override swaps first_message to the English greeting. Pass
  // unconditionally so the agent gets an explicit signal; single-language
  // agents ignore it. Shape matches ElevenLabs' ConversationConfigOverride
  // (see @elevenlabs/types/generated/asyncapi-types.d.ts).
  const sessionLanguageOverride = { agent: { language } };

  const startCall = async () => {
    setErrorMsg(null);
    setTranscript([]);
    setTrackingLinkFired(false);
    setInquiryLinkFired(false);
    setMenuLinkFired(false);
    setHasEnded(false);
    setMode('voice');
    setNeedsVoiceRepair(false);
    // Reset live-transfer state so a fresh session can fire the tool again.
    // Without this, a customer who completed a transfer in a previous session
    // (same page mount) would have stage='done' and the request_live_agent
    // handler would silently no-op on the next fire.
    setLiveTransferState({ stage: 'idle' });
    liveTransferCancelledRef.current = false;
    // Fresh tracking_id for this conversation. Must happen BEFORE
    // buildDynamicVariables() is read into startSession, since the agent's
    // open_tracking_link tool + the post-call beacon both resolve against
    // trackingIdRef.current and would otherwise reuse the previous
    // conversation's UUID within the same page mount.
    trackingIdRef.current = uuidv7();
    // Pre-warm the browser's AudioContext inside the click user-gesture, BEFORE
    // the WebSocket connects. Addresses the "first 1-2 seconds of the welcome
    // message cut off" QA finding — the SDK creates its own context and resumes
    // it AFTER streaming starts, so the opening frames can arrive before playback
    // is ready. Priming with a silent buffer wakes the audio hardware so the
    // SDK's context resumes immediately.
    primeAudioContext();

    // Pre-request mic permission BEFORE fetching the signed URL. The
    // fetchSignedUrl network call (~200-500ms) becomes the natural
    // audio-sink settling window between mic-grant and startSession,
    // closing the iOS first-syllable cutoff race that synthetic
    // setTimeouts couldn't cover. See Round 5 plan.
    let micGranted = false;
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      micGranted = true;
    } catch {
      setErrorMsg('Mic blocked — you can still chat using text below.');
    }

    // Fetch a branch-aware signed URL. If it fails after retries, fail loud —
    // we do NOT fall back to a bare agentId connection (that lands on the Main
    // branch, whose post-call webhook points at the wrong environment and
    // silently loses the booking — see the fetchSignedUrl comment).
    const signedUrl = await fetchSignedUrl(apiBase, venueId);
    if (!signedUrl) {
      setErrorMsg('Connection error. Please try again.');
      setMode(null);
      return;
    }
    const sessionConfig = { signedUrl, connectionType: 'websocket' as const };

    await new Promise<void>((resolve) => setTimeout(resolve, FIRST_MESSAGE_AUDIO_GRACE_MS));
    conversation.startSession({
      ...sessionConfig,
      ...(micGranted ? {} : { textOnly: true }),
      dynamicVariables: buildDynamicVariables(),
      overrides: micGranted
        ? sessionLanguageOverride
        : { ...sessionLanguageOverride, conversation: { textOnly: true } },
    });
  };

  // Loud Room mode — same as startCall but mic STARTS muted. The caller
  // uses a big push-to-talk button in ActiveState to unmute only while
  // they speak. Agent TTS plays normally; background bar noise doesn't
  // trigger VAD or fight for the agent's speaking slot.
  const startLoudRoom = async () => {
    setErrorMsg(null);
    setTranscript([]);
    setTrackingLinkFired(false);
    setInquiryLinkFired(false);
    setMenuLinkFired(false);
    setHasEnded(false);
    setMode('loud');
    setNeedsVoiceRepair(false);
    setShowLoudRoomHint(true);
    setLiveTransferState({ stage: 'idle' });
    liveTransferCancelledRef.current = false;
    trackingIdRef.current = uuidv7();
    primeAudioContext();

    // Pre-request mic permission BEFORE fetching the signed URL — same
    // Round 5 cutoff fix as startCall. Loud Room is voice-only; if mic
    // is blocked we return to mode=null with a chat-mode hint, matching
    // the pre-Round-5 behavior.
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErrorMsg('Mic blocked — try Chat mode instead.');
      setMode(null);
      return;
    }

    const signedUrl = await fetchSignedUrl(apiBase, venueId);
    if (!signedUrl) {
      setErrorMsg('Connection error. Please try again.');
      setMode(null);
      return;
    }
    const sessionConfig = { signedUrl, connectionType: 'websocket' as const };

    await new Promise<void>((resolve) => setTimeout(resolve, FIRST_MESSAGE_AUDIO_GRACE_MS));
    conversation.startSession({ ...sessionConfig, dynamicVariables: buildDynamicVariables(), overrides: sessionLanguageOverride });
    // Mute immediately — the session will still open and receive agent
    // audio, but the caller's mic output will be zero-filled at the
    // worklet layer until they press-and-hold the push-to-talk button.
    // Small delay so setSession's worklet is up before we post the
    // setMuted message.
    setTimeout(() => {
      conversation.setMuted(true);
    }, 50);
  };

  const startChat = async () => {
    setErrorMsg(null);
    setTranscript([]);
    setTrackingLinkFired(false);
    setInquiryLinkFired(false);
    setMenuLinkFired(false);
    setHasEnded(false);
    setMode('text');
    setNeedsVoiceRepair(false);
    setLiveTransferState({ stage: 'idle' });
    liveTransferCancelledRef.current = false;
    trackingIdRef.current = uuidv7();
    // textOnly: true tells the SDK to create a TextConversation (no mic prompt,
    // no AudioContext, no TTS output). The agent still receives sendUserMessage
    // calls and responds with onMessage — perfect for a text-only chat flow.
    // The agent's security settings already permit this runtime override
    // (platform_settings.overrides.conversation_config_override.conversation.text_only = true).
    const signedUrl = await fetchSignedUrl(apiBase, venueId);
    if (!signedUrl) {
      setErrorMsg('Connection error. Please try again.');
      setMode(null);
      return;
    }
    const sessionConfig = { signedUrl, connectionType: 'websocket' as const };
    conversation.startSession({
      ...sessionConfig,
      textOnly: true,
      dynamicVariables: buildDynamicVariables(),
      // Merge textOnly (existing) with language override so chat sessions
      // still pick up the caller's selected language.
      overrides: { ...sessionLanguageOverride, conversation: { textOnly: true } },
    });
  };

  const sendText = () => {
    const text = textInput.trim();
    if (!text) return;
    conversation.sendUserMessage(text);
    setTranscript((prev) => [...prev, { role: 'user', text }]);
    setTextInput('');
    // Voice + Loud Room echo: flash the typed text as a fading bubble over
    // the wave so the caller can confirm what they sent. Both sub-modes
    // mount the VoiceStage (transcript is hidden by default), so they
    // both need the echo. Text mode renders the transcript inline — no
    // echo needed. The render site in VoiceStage already gates on
    // !showAudioFallback so tapping "No audio?" suppresses the ghost.
    // crypto.randomUUID gives each echo a stable id so a later setTimeout
    // only clears its own entry.
    if (mode === 'voice' || mode === 'loud') {
      const id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Date.now());
      setTypedEcho({ text, id });
    }
  };

  // Clear the typed echo ~2.5s after it appears. The guard on `id` matching
  // ensures we don't clear a newer echo that was set while the older one was
  // still counting down.
  useEffect(() => {
    if (!typedEcho) return;
    const targetId = typedEcho.id;
    const t = setTimeout(() => {
      setTypedEcho((current) => (current?.id === targetId ? null : current));
    }, 2500);
    return () => clearTimeout(t);
  }, [typedEcho]);

  // Auto-dismiss the Loud Room explanation hint after 6 seconds. Gives the
  // caller enough time to read "mic light may stay on — that's normal"
  // without lingering past useful.
  useEffect(() => {
    if (!showLoudRoomHint) return;
    const t = setTimeout(() => setShowLoudRoomHint(false), 6000);
    return () => clearTimeout(t);
  }, [showLoudRoomHint]);

  const handleEnd = () => {
    conversation.endSession();
  };

  // Mid-session mode switch. Ends the current session (suppressing its booking-
  // request creation) and starts a new one in the target mode. The previous
  // transcript is replayed to the new session via sendContextualUpdate so the
  // agent knows what was already discussed.
  const handleSwitchMode = (target: WidgetMode) => {
    setErrorMsg(null);
    // Serialize the current transcript as a brief summary the agent can read.
    // Keep it under ~2KB to stay inside ElevenLabs contextual update limits.
    // Filter out 'link' role messages — they're UI affordances, not dialogue,
    // and the agent would misread them as customer utterances ("Tap to open...").
    if (transcript.length > 0) {
      const summary = transcript
        .filter((m) => m.role !== 'link')
        .slice(-30) // last 30 turns is plenty of context
        .map((m) => `${m.role === 'agent' ? 'Agent' : 'Customer'}: ${m.text}`)
        .join('\n');
      pendingContextRef.current =
        `The customer just switched from ${mode === 'text' ? 'text chat' : 'voice'} to ${target === 'text' ? 'text chat' : 'voice'}. ` +
        `Here's the conversation so far — pick up where you left off and DO NOT re-ask for any fields the customer already answered:\n\n` +
        summary;
    }
    // Reset link-fire guards — the new session should be free to share links
    // again if the agent decides to re-surface them after context replay.
    setTrackingLinkFired(false);
    setInquiryLinkFired(false);
    setMenuLinkFired(false);
    switchingModeRef.current = true;
    pendingModeRef.current = target;
    conversation.endSession();
  };

  if (!isActive && hasEnded && transcript.length > 0) {
    // "Ask another question" returns to IdleState so the caller sees the
    // Call / Chat picker again — NOT an auto-start of a new text session.
    // Otherwise the user is trapped: minimize keeps EndedState open, so
    // they can't reach the main pills without a full reset.
    const resetToIdle = () => {
      setTranscript([]);
      setTrackingLinkFired(false);
      setInquiryLinkFired(false);
      setMenuLinkFired(false);
      setHasEnded(false);
      setMode(null);
      setErrorMsg(null);
      // Reset live-transfer state so the next session can fire request_live_agent
      // freshly. Without this, the client guard sees stage='done' from the prior
      // session and silently no-ops the tool fire.
      setLiveTransferState({ stage: 'idle' });
      liveTransferCancelledRef.current = false;
    };
    return (
      <EndedState
        venueName={selectedLocationOption?.name ?? venueName}
        theme={theme}
        transcript={transcript}
        transcriptRef={transcriptRef}
        onTranscriptScroll={handleTranscriptScroll}
        onRestart={resetToIdle}
        onMinimize={onMinimize}
        logoHasOwnBorder={logoHasOwnBorder}
        wasSubmitted={trackingLinkFired}
        fillContainer={fillContainer}
        t={t}
      />
    );
  }

  if (!isActive && requireLocationPick && !selectedLocation && locations && locations.length > 0) {
    return (
      <LocationPickState
        venueName={venueName}
        theme={theme}
        locations={locations}
        onPick={setSelectedLocation}
        onMinimize={onMinimize}
        titleOverride={titleOverride}
        descriptionOverride={descriptionOverride}
        logoHasOwnBorder={logoHasOwnBorder}
      />
    );
  }

  if (!isActive) {
    const handleLangChange = (next: SupportedLang) => {
      setLanguage(next);
      writeStoredLang(next);
    };
    // For multi-location venues, surface a "← Switch venue" link in the idle
    // header. Rewinds selectedLocation → null, which sends the user back to
    // the picker on the next render. No-op for single-location venues.
    const onSwitchLocation = (requireLocationPick && selectedLocation)
      ? () => setSelectedLocation(null)
      : undefined;
    return (
      <IdleState
        venueName={selectedLocationOption?.name ?? venueName}
        theme={theme}
        onCall={startCall}
        onChat={startChat}
        onLoudRoom={startLoudRoom}
        errorMsg={errorMsg}
        titleOverride={titleOverride}
        descriptionOverride={descriptionOverride}
        chatLabelOverride={chatLabelOverride}
        logoHasOwnBorder={logoHasOwnBorder}
        onMinimize={onMinimize}
        t={t}
        language={language}
        supportedLanguages={supportedLanguages}
        onLanguageChange={handleLangChange}
        onSwitchLocation={onSwitchLocation}
        groupLinks={groupLinkItems}
        groupLinkTarget={groupLinkTarget}
      />
    );
  }

  return (
    <ActiveState
      venueName={selectedLocationOption?.name ?? venueName}
      theme={theme}
      mode={mode ?? 'voice'}
      transcript={transcript}
      transcriptRef={transcriptRef}
      onTranscriptScroll={handleTranscriptScroll}
      textInput={textInput}
      setTextInput={setTextInput}
      onSendText={sendText}
      onEnd={handleEnd}
      onSwitchMode={handleSwitchMode}
      isSpeaking={isSpeaking}
      isMuted={conversation.isMuted}
      onMuteToggle={() => conversation.setMuted(!conversation.isMuted)}
      status={status}
      errorMsg={errorMsg}
      logoHasOwnBorder={logoHasOwnBorder}
      needsVoiceRepair={needsVoiceRepair && mode === 'text'}
      onMinimize={onMinimize}
      typedEcho={typedEcho}
      getOutputVolume={conversation.getOutputVolume}
      getInputVolume={conversation.getInputVolume}
      audioDroppedOut={audioDroppedOut}
      onReconnect={() => handleSwitchMode('voice')}
      showAudioFallback={showAudioFallback}
      setShowAudioFallback={setShowAudioFallback}
      showLoudRoomHint={showLoudRoomHint}
      setShowLoudRoomHint={setShowLoudRoomHint}
      ambienceAudioUrl={ambienceAudioUrl}
      fillContainer={fillContainer}
      speakHint={t('widget.callMode.speakHint')}
    />
  );
}

// ── Idle state: big logo + Call/Chat CTAs ────────────────────────────────────

function LocationPickState({
  venueName,
  theme,
  locations,
  onPick,
  onMinimize,
  titleOverride,
  descriptionOverride,
  logoHasOwnBorder,
}: {
  venueName: string;
  theme: CateringWidgetTheme;
  locations: LocationOption[];
  onPick: (slug: string) => void;
  onMinimize?: () => void;
  titleOverride?: string;
  descriptionOverride?: string;
  logoHasOwnBorder?: boolean;
}) {
  return (
    <div
      className="catering-widget-location-pick"
      style={{
        padding: '32px 24px 28px',
        fontFamily: theme.fontBody,
        color: theme.textPrimary,
        position: 'relative',
      }}
    >
      {onMinimize && (
        <button
          type="button"
          onClick={onMinimize}
          aria-label="Minimize widget"
          title="Minimize"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            color: theme.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronDown style={{ width: 18, height: 18 }} />
        </button>
      )}
      {/* Phase 17: render the venue logo above the title — mirrors IdleState's
          LogoBlock so the location-picker step has the same brand chrome as
          the post-pick call/chat screen. Critical for the hosted /book/<slug>
          surface where the picker is the FIRST thing the user sees on a
          mobile-share link. */}
      {theme.logoUrl && theme.logoDisplayMode === 'wide' ? (
        <div style={{ margin: '0 auto 16px', textAlign: 'center' }}>
          <img
            src={theme.logoUrl}
            alt={venueName}
            style={{
              display: 'block',
              margin: '0 auto',
              maxHeight: theme.logoMaxHeight ?? 56,
              maxWidth: theme.logoMaxWidth ?? 220,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      ) : theme.logoUrl ? (
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            margin: '0 auto 18px',
            overflow: 'hidden',
            border: logoHasOwnBorder ? 'none' : `2px solid ${theme.borderColor}`,
            boxShadow: `0 0 40px ${theme.primaryColor}25`,
            background: theme.logoCircleBackground,
          }}
        >
          <img
            src={theme.logoUrl}
            alt={venueName}
            width={96}
            height={96}
            style={{
              width: '100%',
              height: '100%',
              objectFit: theme.logoObjectFit ?? 'cover',
              display: 'block',
            }}
          />
        </div>
      ) : null}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h2
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 22,
            fontWeight: 500,
            margin: 0,
            marginBottom: 8,
            color: theme.textPrimary,
            letterSpacing: '-0.01em',
          }}
        >
          {titleOverride ?? `Pick your venue`}
        </h2>
        <p
          style={{
            fontSize: 14,
            color: theme.textSecondary,
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {descriptionOverride ?? `Which ${venueName} location is the event for?`}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {locations.map((loc) => (
          <button
            key={loc.slug}
            type="button"
            onClick={() => onPick(loc.slug)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 16px',
              minHeight: 56, /* ≥44pt tap target */
              borderRadius: 12,
              border: `1px solid ${theme.borderColor}`,
              background: theme.surfacePanel,
              color: theme.textPrimary,
              fontFamily: theme.fontBody,
              fontSize: 15,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 120ms ease, background 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.primaryColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.borderColor;
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 16, color: theme.textPrimary }}>
                {loc.name}
              </div>
              {loc.neighborhood && (
                <div style={{ fontSize: 13, color: theme.textSecondary, marginTop: 2 }}>
                  {loc.neighborhood}
                </div>
              )}
            </div>
            {loc.capacityHint && (
              <div
                style={{
                  fontSize: 12,
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  marginLeft: 8,
                }}
              >
                {loc.capacityHint}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function IdleState({
  venueName,
  theme,
  onCall,
  onChat,
  onLoudRoom,
  errorMsg,
  titleOverride,
  descriptionOverride,
  chatLabelOverride,
  logoHasOwnBorder,
  onMinimize,
  t,
  language,
  supportedLanguages,
  onLanguageChange,
  onSwitchLocation,
  groupLinks,
  groupLinkTarget,
}: {
  venueName: string;
  theme: CateringWidgetTheme;
  onCall: () => void;
  onChat: () => void;
  onLoudRoom: () => void;
  errorMsg: string | null;
  titleOverride?: string;
  descriptionOverride?: string;
  chatLabelOverride?: string;
  logoHasOwnBorder?: boolean;
  onMinimize?: () => void;
  t: (key: string, values?: Record<string, string | number>) => string;
  language: SupportedLang;
  supportedLanguages?: SupportedLang[];
  onLanguageChange?: (lang: SupportedLang) => void;
  /**
   * For multi-location venues only — when set, render a "← Switch venue"
   * link in the IdleState header that rewinds selectedLocation → null and
   * sends the user back to the location picker. Single-location venues
   * leave this undefined.
   */
  onSwitchLocation?: () => void;
  /**
   * Hospitality-group sibling pages, rendered as a subtle "other locations?"
   * row of plain hyperlinks at the bottom of the idle card. Each link
   * navigates to the sibling's OWN booking page. Empty/undefined for
   * standalone venues (nothing rendered).
   */
  groupLinks?: Array<{ name: string; href: string }>;
  groupLinkTarget?: '_self' | '_blank';
}) {
  // Show the language pill only when the parent explicitly opted in by
  // passing >= 2 supported languages. Single-language widgets (Akiro,
  // default BookOne) get no toggle — avoids visual clutter on venues
  // that only speak one language anyway.
  const showLangToggle =
    !!supportedLanguages && supportedLanguages.length > 1 && !!onLanguageChange;
  // The toggle is absolutely positioned top-left. Venues WITH a logo get
  // natural clearance (the logo block sits between toggle and title); a
  // logo-less venue (Le Buffet) would render the title straight into the
  // toggle row, so bump the card's top padding past the pill (~44px tall).
  const clearHeaderRow = showLangToggle && !theme.logoUrl;
  return (
    <div
      className="catering-widget-idle"
      style={{
        padding: clearHeaderRow ? '64px 24px 28px' : '32px 24px 28px',
        textAlign: 'center',
        fontFamily: theme.fontBody,
        color: theme.textPrimary,
        position: 'relative',
      }}
    >
      {onSwitchLocation && (
        <div style={{ textAlign: 'left', marginBottom: 20 }}>
          <button
            type="button"
            onClick={onSwitchLocation}
            aria-label="Switch venue — return to location picker"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 999,
              background: theme.surfacePanel,
              border: `1px solid ${theme.borderColor}`,
              color: theme.textSecondary,
              fontFamily: theme.fontBody,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.01em',
              cursor: 'pointer',
              transition: 'color 120ms ease, border-color 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme.textPrimary;
              e.currentTarget.style.borderColor = theme.primaryColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.textSecondary;
              e.currentTarget.style.borderColor = theme.borderColor;
            }}
          >
            <span aria-hidden="true">←</span> Switch venue
          </button>
        </div>
      )}
      {onMinimize && (
        <button
          type="button"
          onClick={onMinimize}
          aria-label="Minimize widget"
          title="Minimize"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'transparent',
            border: `1px solid ${theme.borderColor}`,
            // textPrimary (not textSecondary) so the minimize control is clearly
            // visible even on faint-border presets (Akiro's border is 10% white).
            color: theme.textPrimary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          {/* Explicit `color` on the icon (not just currentColor inheritance) so
              a host site's `svg`/`path` stroke rules can't strip the chevron —
              same light-DOM leak that hid the title on Akiro/Kayao. */}
          <ChevronDown size={16} color={theme.textPrimary} />
        </button>
      )}
      {/* Language toggle — absolute top-left. Segmented pill so each
          language is legible next to its sibling (avoids dropdown
          friction on mobile). Rendered above the logo so callers see
          the toggle before engaging the CTAs — no "wait, can I do this
          in French?" moment. */}
      {showLangToggle && (
        <div
          role="radiogroup"
          aria-label={t('widget.lang.toggle.aria')}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'inline-flex',
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 999,
            overflow: 'hidden',
            fontFamily: theme.fontDisplay,
            zIndex: 2,
          }}
        >
          {supportedLanguages!.map((lang) => {
            const selected = lang === language;
            return (
              <button
                key={lang}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onLanguageChange!(lang)}
                style={{
                  padding: '6px 12px',
                  minHeight: 28,
                  background: selected ? theme.primaryColor : 'transparent',
                  color: selected ? '#FFFFFF' : theme.textSecondary,
                  border: 'none',
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: selected ? 'default' : 'pointer',
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {lang.toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
      {theme.logoUrl && theme.logoDisplayMode === 'wide' ? (
        // Wordmark mode — no circular crop. max-height keeps it compact
        // across desktop + mobile; max-width prevents the image from
        // stretching full-width on wider cards. Centered.
        <div style={{ margin: '0 auto 18px', textAlign: 'center' }}>
          <img
            src={theme.logoUrl}
            alt={venueName}
            style={{
              display: 'block',
              margin: '0 auto',
              maxHeight: theme.logoMaxHeight ?? 56,
              maxWidth: theme.logoMaxWidth ?? 220,
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      ) : theme.logoUrl ? (
        // <img> instead of background-image div so the venue name reaches screen readers
        // and search engines. The container still controls the circular crop + glow.
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            margin: '0 auto 20px',
            overflow: 'hidden',
            border: logoHasOwnBorder ? 'none' : `2px solid ${theme.borderColor}`,
            boxShadow: `0 0 40px ${theme.primaryColor}25`,
            background: theme.logoCircleBackground,
          }}
        >
          <img
            src={theme.logoUrl}
            alt={venueName}
            width={120}
            height={120}
            style={{
              width: '100%',
              height: '100%',
              objectFit: theme.logoObjectFit ?? 'cover',
              display: 'block',
            }}
          />
        </div>
      ) : null}
      <h3
        className="catering-widget-heading"
        style={{
          fontFamily: theme.fontDisplay,
          fontWeight: 600,
          fontSize: 22,
          letterSpacing: '-0.02em',
          marginBottom: 8,
          // Explicit color is REQUIRED: the widget renders in the host page's
          // light DOM, so a venue site's own `h3 { color }` (or gradient
          // `-webkit-text-fill-color`) cascades in and overrides the inherited
          // theme.textPrimary — making the title unreadable on dark-heading
          // sites (Akiro, Kayao). Inline color + text-fill beat host CSS.
          color: theme.textPrimary,
          WebkitTextFillColor: theme.textPrimary,
        }}
      >
        {titleOverride ?? t('widget.idle.title.default', { venueName })}
      </h3>
      <p
        style={{
          fontSize: 14,
          color: theme.textSecondary,
          marginBottom: 24,
          maxWidth: 380,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {descriptionOverride ?? t('widget.idle.description')}
      </p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          type="button"
          onClick={onCall}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 32px',
            borderRadius: 999,
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
            color: theme.userBubbleText ?? '#1A0808',
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 0.15s, box-shadow 0.2s',
          }}
        >
          <Phone size={16} />
          {t('widget.idle.call')}
        </button>
        <button
          type="button"
          onClick={onChat}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 32px',
            borderRadius: 999,
            background: 'transparent',
            color: theme.primaryColor,
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            border: `1px solid ${theme.borderColor}`,
            cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}
        >
          <MessageSquare size={16} />
          {chatLabelOverride ?? t('widget.idle.chat')}
        </button>
      </div>
      {/* Loud Room — secondary pill under the main Call/Chat row. Designed
          for bar/restaurant callers: starts a voice session with mic muted
          by default, caller taps a big push-to-talk button to speak.
          Styled as a pill (not an underlined link) so the affordance reads
          unambiguously as "another option you can tap". */}
      <button
        type="button"
        onClick={onLoudRoom}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          margin: '0 auto 14px',
          padding: '10px 20px',
          background: 'transparent',
          color: theme.textSecondary,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: 999,
          cursor: 'pointer',
          fontSize: 13,
          fontFamily: theme.fontDisplay,
          letterSpacing: '0.01em',
          transition: 'border-color 0.2s, color 0.2s',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          minHeight: 40,
        }}
      >
        <MicOff size={16} />
        {t('widget.idle.loudroom')}
      </button>
      <p
        style={{
          fontSize: 11,
          color: theme.textSecondary,
          margin: 0,
          maxWidth: 340,
          marginLeft: 'auto',
          marginRight: 'auto',
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: theme.primaryColor, fontWeight: 600 }}>Call</strong> uses your mic for a voice conversation (you can also type).
        &nbsp;<strong style={{ color: theme.primaryColor, fontWeight: 600 }}>Chat</strong> is text-only — no mic, no audio.
      </p>
      {groupLinks && groupLinks.length > 0 && (
        <GroupLocationLinks
          links={groupLinks}
          label={t('widget.group.other.label')}
          ariaLabel={t('widget.group.other.aria')}
          theme={theme}
          target={groupLinkTarget}
        />
      )}
      {errorMsg && (
        <p style={{ fontSize: 12, color: '#ef4444', marginTop: 14 }}>{errorMsg}</p>
      )}
    </div>
  );
}

// ── Shared header logo (active + ended states) ─────────────────────────────
//
// Renders the venue logo in the active-conversation header AND the ended-
// state header. Two display modes plus a responsive hide-on-narrow rule:
//
// - `theme.logoDisplayMode === 'wide'` → render the wordmark with
//   `objectFit: contain`, sized to fit the available header space.
//   `flexShrink: 1` lets it compress when the buttons (mute, hangup)
//   need room. Caps at 88px wide on desktop.
// - default circle mode → 48×48 round avatar with `objectFit: cover`.
//   Same footprint as before; not affected by responsive logic.
// - widget container width < 360px → logo hidden entirely. Mobile case
//   where every pixel matters for the controls.
//
// Caller must pass `containerWidth` (measured via ResizeObserver in the
// parent state component). See ActiveState / EndedState for the wiring.
function WidgetHeaderLogo({
  theme,
  venueName,
  logoHasOwnBorder,
  containerWidth,
}: {
  theme: CateringWidgetTheme;
  venueName: string;
  logoHasOwnBorder?: boolean;
  containerWidth: number;
}) {
  if (!theme.logoUrl) return null;
  // Hide entirely on narrow widget containers (iPhone SE-class) so the
  // mute + hangup buttons never get pushed off-screen. Threshold tuned to
  // match the bulk of mobile widget renders we've observed.
  if (containerWidth > 0 && containerWidth < 360) return null;

  if (theme.logoDisplayMode === 'wide') {
    // Wide wordmark — `flexShrink: 1` + `min-width: 0` lets the logo
    // compress when the buttons need space. `maxWidth` clamps growth on
    // wide containers. The image inside uses `contain` so the wordmark
    // is never cropped, just scaled.
    return (
      <div
        style={{
          height: 40,
          maxWidth: 88,
          minWidth: 0,
          flexShrink: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <img
          src={theme.logoUrl}
          alt={venueName}
          style={{
            maxHeight: 40,
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    );
  }

  // Default circle avatar (Members Only, Akiro, BookOne default).
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        overflow: 'hidden',
        border: logoHasOwnBorder ? 'none' : `2px solid ${theme.borderColor}`,
        flexShrink: 0,
        background: theme.logoCircleBackground,
      }}
    >
      <img
        src={theme.logoUrl}
        alt={venueName}
        width={48}
        height={48}
        // Respect the per-preset `logoObjectFit` token. Akiro overrides to
        // 'contain' because its trigger logo is the wide AKIRO wordmark —
        // 'cover' crops to the middle letters ("IR" of "AKIRO" on /book/akiro).
        // Square avatars (Members Only, Boxcar, MáLà) keep the 'cover' default.
        style={{ width: '100%', height: '100%', objectFit: theme.logoObjectFit ?? 'cover', display: 'block' }}
      />
    </div>
  );
}

// Hook: observe element width via ResizeObserver. Returns 0 until first
// measurement. Disconnects on unmount.
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// ── Active state: status + transcript + text input ──────────────────────────

function ActiveState({
  venueName,
  theme,
  mode,
  transcript,
  transcriptRef,
  onTranscriptScroll,
  textInput,
  setTextInput,
  onSendText,
  onEnd,
  onSwitchMode,
  isSpeaking,
  isMuted,
  onMuteToggle,
  status,
  errorMsg,
  logoHasOwnBorder,
  needsVoiceRepair,
  onMinimize,
  typedEcho,
  getOutputVolume,
  getInputVolume,
  audioDroppedOut,
  onReconnect,
  showAudioFallback,
  setShowAudioFallback,
  showLoudRoomHint,
  setShowLoudRoomHint,
  ambienceAudioUrl,
  fillContainer = false,
  speakHint,
}: {
  venueName: string;
  theme: CateringWidgetTheme;
  mode: WidgetMode;
  speakHint: string;
  transcript: TranscriptMessage[];
  transcriptRef: React.RefObject<HTMLDivElement | null>;
  onTranscriptScroll: React.UIEventHandler<HTMLDivElement>;
  textInput: string;
  setTextInput: (v: string) => void;
  onSendText: () => void;
  onEnd: () => void;
  onSwitchMode: (target: WidgetMode) => void;
  isSpeaking: boolean;
  isMuted: boolean;
  onMuteToggle: () => void;
  status: string;
  errorMsg: string | null;
  logoHasOwnBorder?: boolean;
  needsVoiceRepair: boolean;
  onMinimize?: () => void;
  typedEcho: { text: string; id: string } | null;
  getOutputVolume: () => number;
  getInputVolume: () => number;
  audioDroppedOut: boolean;
  onReconnect: () => void;
  showAudioFallback: boolean;
  setShowAudioFallback: (v: boolean) => void;
  showLoudRoomHint: boolean;
  setShowLoudRoomHint: (v: boolean) => void;
  ambienceAudioUrl: string;
  fillContainer?: boolean;
}) {
  const isTextMode = mode === 'text';
  const isLoudRoom = mode === 'loud';
  const statusLabel =
    status === 'connecting'
      ? (isTextMode ? 'Switching to chat...' : 'Switching to voice...')
      : isTextMode
        ? 'Ready — type below'
        : isLoudRoom
          ? (isMuted ? 'Loud Room — hold to talk' : 'Mic live — release to mute')
          : isSpeaking
            ? 'Speaking...'
            : 'Listening...';

  // Observe the widget's outer width so the header logo can hide on
  // narrow mobile (< 360px) and stay compact on desktop. Avoids the
  // mute + hangup buttons getting pushed off-screen.
  const outerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useElementWidth(outerRef);

  return (
    <div
      ref={outerRef}
      style={{
        padding: '28px 20px 24px',
        fontFamily: theme.fontBody,
        color: theme.textPrimary,
        // Flex-fill mode: become a flex column so the transcript can grow to
        // fill available space and the input row pins to the bottom. Used
        // by fullscreen surfaces (the book link, the corner pill expanded
        // on mobile) so the chat behaves like iMessage. Default block flow
        // preserved for surfaces with unconstrained-height parents.
        ...(fillContainer && {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }),
      }}
    >
      {/* Low-volume restaurant ambience loop — plays continuously while a
          voice session is active. Two purposes:
          1. Keeps the browser audio session "warm" at the OS layer, which
             helps prevent Chrome + iOS Safari from silently suspending the
             agent's TTS playback when the page backgrounds or a device
             swaps (the empty-silence trick didn't register as audio
             strongly enough; a real low-amplitude audio source does).
          2. Adds subtle Akiro vibe — the caller hears a quiet restaurant
             ambience behind the hostess, which reads as "you're at a bar
             already" rather than a sterile VoIP call.
          iOS Safari IGNORES the HTML5 audio.volume property entirely
          (Apple: volume is controlled only by hardware buttons). So
          setting `el.volume = 0.x` here did nothing on iPhone — the
          ambience played at full file amplitude, which was way too loud.
          Fix: the amplitude is now BAKED INTO the MP3 itself at ~3% of
          the original via ffmpeg (-filter:a volume=0.03). That means iOS
          plays it at whisper level regardless of the volume prop. We still
          set volume=1.0 here so desktop Chrome / Android / Firefox also
          plays the baked-in level (not further attenuated). Gated on
          mode === 'voice' so text-mode stays silent. */}
      {!isTextMode && (
        <audio
          src={ambienceAudioUrl}
          loop
          autoPlay
          playsInline
          preload="auto"
          ref={(el) => { if (el) el.volume = 1.0; }}
          style={{ display: 'none' }}
        />
      )}
      {/* Header: logo + status + end button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 18,
          ...(fillContainer && { flexShrink: 0 }),
        }}
      >
        <WidgetHeaderLogo
          theme={theme}
          venueName={venueName}
          logoHasOwnBorder={logoHasOwnBorder}
          containerWidth={containerWidth}
        />
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {venueName}
          </div>
          <div
            style={{
              fontSize: 12,
              color: theme.primaryColor,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 2,
            }}
          >
            <PulseDot color={theme.primaryColor} inactiveColor={theme.textSecondary} active={isTextMode ? true : !isMuted} />
            {statusLabel}
          </div>
        </div>
        {/* "No audio?" escape hatch — voice-mode only. When audio fails
            silently (frames arrive but playback is muted at the OS layer,
            which our dropout watchdog can't detect), tap this to reveal the
            running transcript over the wave with a "Can't hear? Type below"
            hint. The voice session stays active — nothing restarts, context
            is preserved, and the caller can keep talking via the text input
            if they want. Tap again to hide. */}
        {!isTextMode && (
          <button
            type="button"
            aria-label={showAudioFallback ? 'Hide transcript' : 'No audio?'}
            title={showAudioFallback ? 'Hide transcript' : 'No audio?'}
            onClick={() => setShowAudioFallback(!showAudioFallback)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 14px',
              borderRadius: 999,
              background: showAudioFallback ? `${theme.primaryColor}22` : 'transparent',
              border: `1px solid ${theme.borderColor}`,
              color: showAudioFallback ? theme.primaryColor : theme.textSecondary,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: theme.fontDisplay,
              letterSpacing: '0.02em',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              whiteSpace: 'nowrap',
              minHeight: 40,
            }}
          >
            <MessageCircle size={12} />
            {showAudioFallback ? 'Hide' : 'No audio?'}
          </button>
        )}
        {/* Minimize — first control in the row so it doesn't collide with the
            red End button or the mic/mode toggles. Only rendered when a
            wrapper (FloatingWidget) passes onMinimize; inline placements
            where there's nothing to collapse leave it undefined. */}
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            aria-label="Minimize widget"
            title="Minimize"
            style={{
              padding: 10,
              borderRadius: 8,
              background: 'transparent',
              border: `1px solid ${theme.borderColor}`,
              color: theme.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
              minHeight: 40,
            }}
          >
            <ChevronDown size={16} />
          </button>
        )}
        {/* Mic mute toggle — only relevant in voice mode. Text sessions have no mic. */}
        {!isTextMode && (
          <button
            type="button"
            onClick={onMuteToggle}
            aria-label={isMuted ? 'Unmute mic' : 'Mute mic'}
            style={{
              padding: 10,
              borderRadius: 8,
              background: 'transparent',
              border: `1px solid ${theme.borderColor}`,
              color: isMuted ? '#ef4444' : theme.primaryColor,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
              minHeight: 40,
            }}
          >
            {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}
        <button
          type="button"
          onClick={onEnd}
          aria-label={isTextMode ? 'End chat' : 'End call'}
          style={{
            padding: 10,
            borderRadius: 8,
            background: 'transparent',
            border: `1px solid rgba(239, 68, 68, 0.4)`,
            color: '#ef4444',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 40,
            minHeight: 40,
          }}
        >
          {isTextMode ? <X size={16} /> : <PhoneOff size={16} />}
        </button>
      </div>

      {/* Voice mode: no transcript — use a motion graphic instead. Voice-mode
          transcripts are prone to out-of-order rendering (ASR commits after
          agent response streams), and the caller can't read while talking
          anyway. If a link pill landed in the transcript, surface it centered.
          Text mode keeps the transcript as before. */}

      {/* Audio-dropout reconnect pill — voice-mode only. Appears when the
          watchdog detects a 2.5s stall in TTS frames mid-response. Tap →
          reuses handleSwitchMode('voice') which preserves transcript as
          contextual update on the fresh session, so the conversation picks
          up where it left off instead of restarting from the greeting. */}
      {!isTextMode && audioDroppedOut && (
        <button
          type="button"
          onClick={onReconnect}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '10px 16px',
            borderRadius: 10,
            width: '100%',
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
            color: theme.userBubbleText ?? '#1A0808',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: theme.fontDisplay,
            border: 'none',
            cursor: 'pointer',
            marginBottom: 10,
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          Audio cut out — tap to reconnect
        </button>
      )}

      {!isTextMode ? (
        <VoiceStage
          theme={theme}
          isSpeaking={isSpeaking}
          linkBubble={transcript.filter((m) => m.role === 'link').slice(-1)[0] ?? null}
          transcript={transcript}
          showAudioFallback={showAudioFallback}
          typedEcho={typedEcho}
          getOutputVolume={getOutputVolume}
          getInputVolume={getInputVolume}
        />
      ) : (
      <div
        ref={transcriptRef}
        onScroll={onTranscriptScroll}
        style={{
          // Default: fixed-height transcript box (used by the iframe widget,
          // venue/catering profiles, demo center variants — surfaces with
          // unconstrained-height parents where flex-fill would collapse).
          // fillContainer mode: transcript grows to fill the flex column,
          // header + input row pin top/bottom. Used by the book link + the
          // corner pill expanded on mobile.
          ...(fillContainer
            ? { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }
            : { minHeight: 240, maxHeight: 320, overflowY: 'auto' }),
          padding: '14px 16px',
          borderRadius: 12,
          background: theme.surfacePanel,
          border: `1px solid ${theme.borderColor}`,
          marginBottom: 12,
        }}
      >
        {transcript.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: theme.textSecondary,
              textAlign: 'center',
              padding: '40px 0',
              fontStyle: 'italic',
            }}
          >
            Chat starting... type a message below to begin.
          </div>
        ) : (
          transcript.map((m, i) => {
            if (m.role === 'link' && m.href) {
              // Inline tappable link bubble. LinkPill hardens the tap for
              // mobile (touch-action: manipulation + sync window.open) so the
              // first tap always opens the booking page.
              return (
                <div
                  key={i}
                  style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}
                >
                  <LinkPill href={m.href} text={m.text} theme={theme} />
                </div>
              );
            }
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    // Agent = plain white text, no background, no border — the
                    // emphasis belongs on the user's input (what they typed),
                    // not on the agent's replies. Prevents the "who said
                    // what?" ambiguity of matching bubble weights.
                    // User = gold gradient pill as before.
                    maxWidth: m.role === 'user' ? '85%' : '100%',
                    padding: m.role === 'user' ? '8px 14px' : '4px 0',
                    borderRadius: m.role === 'user' ? 14 : 0,
                    background:
                      m.role === 'user'
                        ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`
                        : 'transparent',
                    color: m.role === 'user' ? (theme.userBubbleText ?? '#1A0808') : theme.textPrimary,
                    fontSize: 14,
                    fontFamily: theme.fontBody,
                    lineHeight: 1.45,
                    border: 'none',
                  }}
                >
                  {m.text}
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Voice-repair banner — shown when we detect the caller fell out of voice
          mode after silence (agent said "still there?" or user typed "can't hear
          you"). Clicking switches back to voice and replays the transcript as
          context so the agent picks up where it left off. */}
      {needsVoiceRepair && (
        <button
          type="button"
          onClick={() => onSwitchMode('voice')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 16px',
            borderRadius: 10,
            width: '100%',
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
            color: theme.userBubbleText ?? '#1A0808',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: theme.fontDisplay,
            border: 'none',
            cursor: 'pointer',
            marginBottom: 12,
          }}
        >
          <Mic size={16} />
          Switch back to voice
        </button>
      )}

      {/* Loud Room push-to-talk — big, easy-tap button stacked ABOVE the
          text input row when mode === 'loud' (so a caller can still type
          their email while in Loud Room — the agent asks for it mid-intake).
          Hold = unmute the mic while talking; release = mute immediately.
          Touch + mouse + pen via pointer events. Aria-pressed reflects mute
          state for a11y. A one-time info pill above explains the iOS/Chrome
          mic-indicator quirk (browser keeps the OS mic light on even though
          the agent hears nothing — worklet soft-mute). */}
      {isLoudRoom && showLoudRoomHint && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '10px 12px',
            marginBottom: 10,
            borderRadius: 10,
            background: `${theme.primaryColor}14`,
            border: `1px solid ${theme.borderColor}`,
            color: theme.textSecondary,
            fontSize: 12,
            lineHeight: 1.45,
            fontFamily: theme.fontBody,
          }}
        >
          <Info size={14} style={{ color: theme.primaryColor, flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            Your phone&apos;s mic light may stay on — that&apos;s normal. The agent hears nothing until you hold to talk.
          </div>
          <button
            type="button"
            aria-label="Dismiss hint"
            onClick={() => setShowLoudRoomHint(false)}
            style={{
              padding: 2,
              background: 'transparent',
              border: 'none',
              color: theme.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
      {isLoudRoom && (
        <button
          type="button"
          aria-label={isMuted ? 'Hold to talk' : 'Release to mute'}
          aria-pressed={!isMuted}
          onPointerDown={(e) => { e.preventDefault(); onMuteToggle(); }}
          onPointerUp={() => { if (!isMuted) onMuteToggle(); }}
          onPointerCancel={() => { if (!isMuted) onMuteToggle(); }}
          onPointerLeave={() => { if (!isMuted) onMuteToggle(); }}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            width: '100%',
            padding: '24px 28px',
            borderRadius: 18,
            background: isMuted
              ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`
              : '#ef4444',
            color: theme.userBubbleText ?? '#1A0808',
            fontSize: 17,
            fontWeight: 700,
            fontFamily: theme.fontDisplay,
            letterSpacing: '0.01em',
            border: 'none',
            cursor: 'pointer',
            touchAction: 'manipulation',
            marginBottom: 12,
            // iOS long-press otherwise triggers text-selection / callout
            // menu which steals the pointerup event and looks broken.
            // WebKit-prefixed needed on Safari; unprefixed for Chrome.
            WebkitTapHighlightColor: 'transparent',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            userSelect: 'none',
            boxShadow: isMuted
              ? `0 0 28px ${theme.primaryColor}55`
              : '0 0 28px rgba(239, 68, 68, 0.5)',
            transition: 'background 0.15s, box-shadow 0.15s',
          }}
        >
          {isMuted ? <Mic size={24} style={{ pointerEvents: 'none' }} /> : <MicOff size={24} style={{ pointerEvents: 'none' }} />}
          <span style={{ pointerEvents: 'none' }}>{isMuted ? 'Hold to talk' : 'Release to mute'}</span>
        </button>
      )}
      {/* Call-mode "you can talk" reminder: in voice/loud mode the agent asks the
          caller to TYPE their email + number mid-intake — make it obvious they can
          just SAY them out loud instead (mics are live in both modes). Hidden in
          text mode (no mic there). */}
      {mode !== 'text' && (
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            marginBottom: 8,
            borderRadius: 9,
            background: `${theme.primaryColor}10`,
            border: `1px solid ${theme.borderColor}`,
            color: theme.textSecondary,
            fontSize: 12,
            lineHeight: 1.4,
            fontFamily: theme.fontBody,
          }}
        >
          <Mic size={13} style={{ color: theme.primaryColor, flexShrink: 0 }} />
          <span>{speakHint}</span>
        </div>
      )}

      {/* Text input — always rendered (even in Loud Room) so callers can type
          an email / longer message while in push-to-talk mode. */}
      <div style={{ display: 'flex', gap: 8, minWidth: 0, ...(fillContainer && { flexShrink: 0 }) }}>
        <input
          type="text"
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSendText();
            }
          }}
          placeholder="Type a message..."
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 10,
            background: theme.surfacePanel,
            border: `1px solid ${theme.borderColor}`,
            color: theme.textPrimary,
            // 16px floor — iOS Safari + IG in-app browser auto-zoom on
            // input focus when rendered font-size < 16px. The global CSS
            // rule in globals.css can't override this inline style, so
            // the floor lives here. Don't drop it below 16.
            fontSize: 16,
            fontFamily: theme.fontBody,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={onSendText}
          aria-label="Send message"
          disabled={!textInput.trim()}
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            background: textInput.trim()
              ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`
              : theme.sendButtonDisabledBg ?? 'rgba(51, 21, 21, 0.45)',
            // Submit icon color follows the per-venue userBubbleText token
            // — same contrast pattern the user-typed chat bubbles use. Fallback
            // to the prior #1A0808 for presets that don't define one (Akiro,
            // Members Only, Boxcar, AndSon all read fine on their light
            // primary/accent gradient). MáLà (yellow on purple) + Ottavio
            // (white on red) both get readable per-brand colors via this path.
            color: textInput.trim() ? (theme.userBubbleText ?? '#1A0808') : theme.textSecondary,
            border: 'none',
            cursor: textInput.trim() ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          <Send size={16} />
        </button>
      </div>

      {errorMsg && (
        <p style={{ fontSize: 12, color: '#ef4444', marginTop: 10 }}>{errorMsg}</p>
      )}
    </div>
  );
}

// ── Voice stage: motion graphic instead of transcript ─────────────────────
//
// Replaces the transcript scroll area when mode === 'voice'. Two reasons:
//   1. Voice transcripts rendered out-of-order (ASR commit races the agent
//      response stream — caller saw agent replies before their own turn
//      appeared), and we can't reliably fix that at the SDK level.
//   2. Nobody reads a transcript while they're actively talking. The focus
//      should be the audio experience — a calm "listening" vs "speaking"
//      indicator — not a running log.
//
// When the agent fires open_tracking_link, the link bubble propagates here
// as `linkBubble` and takes over the stage centered so the caller can tap it.

function VoiceStage({
  theme,
  isSpeaking,
  linkBubble,
  transcript,
  showAudioFallback,
  typedEcho,
  getOutputVolume,
  getInputVolume,
}: {
  theme: CateringWidgetTheme;
  isSpeaking: boolean;
  linkBubble: TranscriptMessage | null;
  transcript: TranscriptMessage[];
  showAudioFallback: boolean;
  typedEcho: { text: string; id: string } | null;
  getOutputVolume: () => number;
  getInputVolume: () => number;
}) {
  const fallbackScrollRef = useRef<HTMLDivElement>(null);
  // Auto-scroll the fallback overlay to the latest message when it opens or
  // when a new entry arrives while it's open. Deferred to rAF so the new
  // message has laid out first — a synchronous scrollTop= lands short on
  // mobile Safari (same fix as the main transcript above).
  useEffect(() => {
    const el = fallbackScrollRef.current;
    if (!showAudioFallback || !el) return;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [showAudioFallback, transcript]);
  const waveRef = useRef<BookOneWaveHandle>(null);

  // Palette swap: lighter while the agent is listening, darker/branded
  // while the agent is speaking. Both palettes in a pair come from the
  // same brand family so the cross-fade (setPalette interpolates the 7
  // gradient stops internally) reads as one continuous brand moment,
  // not a hard colour switch. Pair resolution:
  //   - 'bookone' / 'bookoneLight' family → bookoneLight ↔ bookone
  //   - 'akiro' / 'akiroLight' / 'akiroDark' family → akiroLight ↔ akiroDark
  //   - anything else → flat, no swap (one-off palettes without a light/dark pair)
  useEffect(() => {
    const base = theme.wavePalette ?? 'akiroLight';
    let listening: PaletteName = base;
    let speaking: PaletteName = base;
    if (base === 'bookone' || base === 'bookoneLight') {
      listening = 'bookoneLight';
      speaking = 'bookone';
    } else if (base === 'akiroLight' || base === 'akiro' || base === 'akiroDark') {
      listening = 'akiroLight';
      speaking = 'akiroDark';
    } else if (base === 'ottavio' || base === 'ottavioLight') {
      listening = 'ottavioLight';
      speaking = 'ottavio';
    } else if (base === 'glowbal' || base === 'glowbalLight') {
      // Glowbal parent — magenta-purple on grey while the AGENT speaks; flips
      // to WHITE (whiteHold) while the CUSTOMER speaks. The whole Glowbal group
      // shares this "brand-color ↔ white" pattern (a clean, legible listening
      // state) instead of the old "just gets lighter" same-hue dim. The bright
      // magenta-purple identity is unmistakable when the agent talks; white is
      // unmistakable when you talk.
      listening = 'whiteHold';
      speaking = 'glowbal';
    } else if (base === 'cyan' || base === 'blue' || base === 'green' || base === 'orange') {
      // Glowbal sister venues on the generic ElevenLabs palettes — Coast +
      // Five Sails (cyan, sea-glass), Italian Kitchen (blue, navy), Trattoria
      // (green), Riley's (orange, amber). Each keeps its brand color while the
      // AGENT speaks and flips to WHITE (whiteHold) while the CUSTOMER speaks.
      // These palettes already have white EDGES, so the cross-fade only moves
      // the colored core → white — no edge motion, a crisp brand↔white swap.
      listening = 'whiteHold';
      speaking = base;
    } else if (base === 'blackGold' || base === 'blackGoldWhite') {
      // Glowbal steakhouses — The Roof + Black + Blue. Gold/amber core on black
      // while the AGENT speaks (blackGold); the core flips gold → WHITE while
      // the CUSTOMER speaks (blackGoldWhite), edges stay black so the swap is
      // edge-stable. Separate from The Delancey NYC's `delancey` palette (which
      // keeps its own cream-flip) to avoid cross-venue blast radius.
      listening = 'blackGoldWhite';
      speaking = 'blackGold';
    } else if (base === 'baccala') {
      // Baccalà — white→teal seafood/wine ribbon. Teal core while the AGENT
      // speaks (baccala); flips to WHITE (whiteHold) while the CUSTOMER speaks.
      // The baccala palette already has white edges, so only the core moves
      // teal → white — an edge-stable brand↔white swap.
      listening = 'whiteHold';
      speaking = 'baccala';
    } else if (base === 'mala') {
      // MáLà — listening state swaps to the white-anchored `neutral` palette
      // so the wave reads as "I'm listening, I'm yours" while the caller
      // speaks. Agent-speaking returns to the colorful 4-location MáLà
      // palette. Matches the Akiro pattern (light ↔ branded) without
      // introducing a new palette file.
      listening = 'neutral';
      speaking = 'mala';
    } else if (base === 'clandestino' || base === 'clandestinoLight') {
      // Clandestino — speakeasy red on black. Listening state cores on
      // ember-dark (clandestinoLight) for the "I'm holding the moment"
      // feel; speaking returns to full brand-red (clandestino). Pattern
      // mirrors Akiro: same hue family, lighter when caller speaks /
      // saturated when agent responds.
      listening = 'clandestinoLight';
      speaking = 'clandestino';
    } else if (base === 'kayao' || base === 'kayaoLight') {
      // Kayao — Chifa coral on deep teal. Listening cores on cream + pale-
      // coral (kayaoLight); speaking returns to teal + brand-coral
      // (kayao). Same Akiro pattern: lighter while caller speaks,
      // saturated while agent responds.
      listening = 'kayaoLight';
      speaking = 'kayao';
    } else if (base === 'ayayay' || base === 'ayayayLight') {
      // Ayayay — Mexican-Peruvian coral on dark forest green. Listening cores
      // on cream + pale-coral (ayayayLight) echoing the venue's body
      // background; speaking returns to forest-green + brand-coral (ayayay).
      listening = 'ayayayLight';
      speaking = 'ayayay';
    } else if (base === 'matilda' || base === 'matildaLight') {
      // Matilda — minimal cream + black. Listening cores on wood tan
      // (matildaLight) for the held-moment state; speaking returns to
      // pure-black-cored variant (matilda). Mirrors the venue's only-two-
      // colors brand identity.
      listening = 'matildaLight';
      speaking = 'matilda';
    } else if (base === 'exodo' || base === 'exodoLight') {
      // Exodo — pure black + white parent-group palette. Listening cores
      // on mid-grey (exodoLight); speaking returns to white-cored
      // (exodo). Mirrors the same silhouette pattern as Matilda but with
      // higher-contrast cores (no warm wood tone — pure greyscale).
      listening = 'exodoLight';
      speaking = 'exodo';
    } else if (base === 'delancey' || base === 'delanceyLight') {
      // The Delancey NYC — black canvas + cream accent. Listening cores
      // on cream (delanceyLight) for the held-moment state; speaking
      // returns to the warm gold/amber core (delancey). Mirrors the
      // Akiro / Ottavio listening↔speaking pattern (light ↔ branded).
      listening = 'delanceyLight';
      speaking = 'delancey';
    }
    // Defensive optional-chain on the method too, not just the ref. The
    // production install on a third-party host hit
    //   "TypeError: l.current?.setPalette is not a function"
    // when waveRef.current was the next/dynamic loadable wrapper (non-null,
    // no imperative methods) instead of the resolved BookOneWave. The static-
    // import fix below makes this no longer reachable today, but keeping the
    // guard so a future refactor that breaks the contract fails silent
    // instead of crashing the React render and unmounting the entire widget.
    waveRef.current?.setPalette?.(isSpeaking ? speaking : listening);
  }, [isSpeaking, theme.wavePalette]);

  // Volume-reactive amplitude — polled per animation frame. The SDK exposes
  // getOutputVolume() (agent TTS playback level) and getInputVolume() (mic
  // input). We pick whichever channel is active and smooth into the wave's
  // amplitude so the ribbon breathes with the actual audio. This replaces
  // the synthetic "talking" pulse, which the user flagged as jumpy.
  //
  // Idle floor: 1.4 — enough for the ribbon to sit in the vertical center
  //   of the frame on wide desktop viewports too. Lower values anchor the
  //   wave near its baseline and it reads as a thin strip at the bottom.
  // Speaking/listening peak: ~1.8 (IDLE + PEAK_SCALE * 0.3 on loud frames).
  // Smoothing: exponential lerp @ 15% — fast enough to feel reactive,
  //   damped enough to read as silk, not strobe.
  useEffect(() => {
    let rafId = 0;
    let smoothed = 1.4;
    const IDLE = 1.4;
    const PEAK_SCALE = 1.2; // output volume ~0.3 at peaks → amp ~1.8
    const LERP = 0.15;

    const tick = () => {
      const active = isSpeaking ? getOutputVolume() : getInputVolume();
      const target = Math.max(IDLE, IDLE + active * PEAK_SCALE);
      smoothed += (target - smoothed) * LERP;
      waveRef.current?.setAmplitude(smoothed);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isSpeaking, getOutputVolume, getInputVolume]);

  // When the booking link arrives, take over the stage with the pill.
  if (linkBubble && linkBubble.href) {
    return (
      <div
        style={{
          minHeight: 240,
          maxHeight: 320,
          padding: '14px 16px',
          borderRadius: 12,
          background: theme.surfacePanel,
          border: `1px solid ${theme.borderColor}`,
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LinkPill href={linkBubble.href} text={linkBubble.text} theme={theme} large />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        // Responsive height — scales with viewport width. Keeps the frame
        // nearly square on narrow mobile while not filling the whole card
        // on wide desktop. Min 260, max 320, scaling via 28vw between
        // ~930px and ~1145px viewport widths. (Earlier values of 280/360
        // made the wave visually dominate the venue profile card.)
        height: 'clamp(260px, 28vw, 320px)',
        borderRadius: 12,
        // Wave-frame canvas. Defaults to the widget panel; light-canvas venues
        // override with a dark `waveStageBg` so the brand ribbon and its white
        // listening-flip both read (white-on-white would vanish).
        background: theme.waveStageBg ?? theme.surfacePanel,
        border: `1px solid ${theme.borderColor}`,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      {/* The silky ribbon fills the frame — no text, no labels. Amplitude is
          driven by the real getOutputVolume / getInputVolume poll above, so
          the ribbon breathes with the actual audio rather than a synthetic
          pulse. talking=false (SDK talking mode would override the amplitude
          we're trying to set), default speed. */}
      <BookOneWave
        ref={waveRef}
        palette={theme.wavePalette ?? 'akiroLight'}
        height="100%"
        amplitude={1.4}
        speed={0.7}
      />

      {/* "No audio?" fallback overlay. Surfaced via the header pill when the
          caller can't hear the agent. Shows the running transcript scrolled
          to the latest turn + a gentle hint that typing still works. Voice
          session stays active underneath — nothing restarts, no context is
          lost. Overlay is pointer-events: none on the scroll area's children
          but pointer-events: auto on the scroll container itself so the
          transcript is scrollable even while the wave keeps running. */}
      {showAudioFallback && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            // Theme-aware opaque overlay. BookOne default = near-black
            // burgundy mask; Ottavio cream = light mask so its dark
            // text stays readable. Previously hardcoded rgba(16,8,8,0.94)
            // which blacked out every light-themed venue's transcript.
            background: theme.audioFallbackBg ?? 'rgba(16, 8, 8, 0.94)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div
            style={{
              padding: '10px 14px',
              background: `${theme.primaryColor}1A`,
              borderBottom: `1px solid ${theme.borderColor}`,
              color: theme.primaryColor,
              fontSize: 12,
              fontFamily: theme.fontDisplay,
              letterSpacing: '0.02em',
              textAlign: 'center',
            }}
          >
            Can&apos;t hear? Feel free to type below — the agent will keep going.
          </div>
          <div
            ref={fallbackScrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '14px 16px',
            }}
          >
            {transcript.filter((m) => m.role !== 'link').map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: m.role === 'user' ? '85%' : '100%',
                    padding: m.role === 'user' ? '6px 12px' : '2px 0',
                    borderRadius: m.role === 'user' ? 12 : 0,
                    background:
                      m.role === 'user'
                        ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`
                        : 'transparent',
                    color: m.role === 'user' ? (theme.userBubbleText ?? '#1A0808') : theme.textPrimary,
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Typed-message echo — fades in/out so the caller can verify what they
          typed in voice mode (where the transcript is otherwise hidden). */}
      {typedEcho && !showAudioFallback && (
        <div
          key={typedEcho.id}
          style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '85%',
            padding: '8px 14px',
            borderRadius: 14,
            background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
            color: theme.userBubbleText ?? '#1A0808',
            fontSize: 13,
            fontFamily: theme.fontBody,
            lineHeight: 1.4,
            animation: 'typed-echo 2.5s ease-out forwards',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {typedEcho.text}
        </div>
      )}
      <style>{`
        @keyframes typed-echo {
          0%   { opacity: 0; transform: translate(-50%, 8px); }
          10%  { opacity: 1; transform: translate(-50%, 0); }
          75%  { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -4px); }
        }
      `}</style>
    </div>
  );
}

// Booking-link pill, hardened for mobile: `touch-action: manipulation` kills
// the iOS double-tap zoom delay, and a synchronous onClick call to window.open
// inside the user-gesture guarantees the first tap opens the page (some mobile
// Safari versions otherwise drop the target when the anchor is inside a React
// tree with custom handlers).
function LinkPill({
  href,
  text,
  theme,
  large,
}: {
  href: string;
  text: string;
  theme: CateringWidgetTheme;
  large?: boolean;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        // Open synchronously inside the gesture; the <a> target="_blank" is
        // still the fallback for keyboard/right-click/programmatic open.
        try {
          const w = window.open(href, '_blank', 'noopener,noreferrer');
          if (w) e.preventDefault();
        } catch {
          // Let the native anchor handle it.
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: large ? '14px 28px' : '10px 20px',
        borderRadius: 999,
        background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`,
        color: theme.userBubbleText ?? '#1A0808',
        fontSize: large ? 15 : 14,
        fontWeight: 600,
        fontFamily: theme.fontDisplay,
        textDecoration: 'none',
        maxWidth: '90%',
        boxShadow: `0 0 ${large ? 24 : 16}px ${theme.primaryColor}${large ? '40' : '25'}`,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        cursor: 'pointer',
      }}
    >
      {text}
    </a>
  );
}

// ── Ended state: frozen transcript + "Start new conversation" ──────────────

function EndedState({
  venueName,
  theme,
  transcript,
  transcriptRef,
  onTranscriptScroll,
  onRestart,
  onMinimize,
  logoHasOwnBorder,
  wasSubmitted,
  fillContainer = false,
  t,
}: {
  venueName: string;
  theme: CateringWidgetTheme;
  transcript: TranscriptMessage[];
  transcriptRef: React.RefObject<HTMLDivElement | null>;
  onTranscriptScroll: React.UIEventHandler<HTMLDivElement>;
  onRestart: () => void;
  onMinimize?: () => void;
  logoHasOwnBorder?: boolean;
  // Distinguishes a completed intake (agent fired open_tracking_link → backend
  // received the booking) from an early cancel where nothing reached the team.
  // Drives the header status copy so we don't claim "Sent" on a bailed-out chat.
  wasSubmitted: boolean;
  fillContainer?: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  const statusKey = wasSubmitted ? 'widget.ended.status.sent' : 'widget.ended.status.cancelled';
  // Same width-observing logic as ActiveState — keeps the logo behavior
  // consistent across active and ended states (wordmark venues like
  // Otavio render their full logo, narrow mobile hides it).
  const outerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useElementWidth(outerRef);
  return (
    <div
      ref={outerRef}
      style={{
        padding: '28px 20px 24px',
        fontFamily: theme.fontBody,
        color: theme.textPrimary,
        ...(fillContainer && {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }),
      }}
    >
      {/* Header — logo + "Conversation ended" indicator + minimize */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, ...(fillContainer && { flexShrink: 0 }) }}>
        <WidgetHeaderLogo
          theme={theme}
          venueName={venueName}
          logoHasOwnBorder={logoHasOwnBorder}
          containerWidth={containerWidth}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: theme.fontDisplay, fontWeight: 600, fontSize: 14 }}>
            {venueName}
          </div>
          <div
            style={{
              fontSize: 12,
              color: theme.primaryColor,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 2,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: theme.primaryColor,
                display: 'inline-block',
              }}
            />
            {t(statusKey)}
          </div>
        </div>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            aria-label="Minimize widget"
            title="Minimize"
            style={{
              padding: 10,
              borderRadius: 8,
              background: 'transparent',
              border: `1px solid ${theme.borderColor}`,
              color: theme.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
              minHeight: 40,
            }}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>

      {/* Frozen transcript — identical rendering to ActiveState so link pills
          remain tappable. The transcript is read-only now (no text input). */}
      <div
        ref={transcriptRef}
        onScroll={onTranscriptScroll}
        style={{
          ...(fillContainer
            ? { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }
            : { minHeight: 240, maxHeight: 320, overflowY: 'auto' }),
          padding: '14px 16px',
          borderRadius: 12,
          background: theme.surfacePanel,
          border: `1px solid ${theme.borderColor}`,
          marginBottom: 12,
        }}
      >
        {transcript.map((m, i) => {
          if (m.role === 'link' && m.href) {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <LinkPill href={m.href} text={m.text} theme={theme} />
              </div>
            );
          }
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  maxWidth: m.role === 'user' ? '85%' : '100%',
                  padding: m.role === 'user' ? '8px 14px' : '4px 0',
                  borderRadius: m.role === 'user' ? 14 : 0,
                  background:
                    m.role === 'user'
                      ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.accentColor})`
                      : 'transparent',
                  color: m.role === 'user' ? (theme.userBubbleText ?? '#1A0808') : theme.textPrimary,
                  fontSize: 14,
                  fontFamily: theme.fontBody,
                  lineHeight: 1.45,
                  border: 'none',
                }}
              >
                {m.text}
              </div>
            </div>
          );
        })}
      </div>

      {/* "Start new conversation" pill — resets transcript + mode. */}
      <button
        type="button"
        onClick={onRestart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 16px',
          borderRadius: 10,
          width: '100%',
          background: 'transparent',
          color: theme.primaryColor,
          fontSize: 14,
          fontWeight: 600,
          fontFamily: theme.fontDisplay,
          border: `1px solid ${theme.borderColor}`,
          cursor: 'pointer',
          ...(fillContainer && { flexShrink: 0 }),
        }}
      >
        <MessageSquare size={16} />
        {t('widget.ended.restart')}
      </button>
    </div>
  );
}

// Number of times to retry the signed-url fetch before giving up. The backend
// already retries the upstream ElevenLabs call; this covers a transient blip
// between the browser and our API (cold start, network hiccup, brief 5xx).
const SIGNED_URL_MAX_ATTEMPTS = 3;

// Fetch a branch-aware signed URL from the API, retrying transient failures.
// Returns null ONLY after every attempt fails. Callers MUST treat null as fatal
// and surface an error — they must NOT fall back to a bare `agentId` connection.
//
// Why no agentId fallback: connecting by bare agentId lands on the agent's
// published Main branch, whose post-call webhook points at PRODUCTION. For a
// staging (or any not-yet-promoted) venue that routes the finished booking to an
// environment where the venue row doesn't exist, so it's silently discarded
// while the customer is told "Sent to the team." That exact silent fallback lost
// a real booking on 2026-06-03 (Black + Blue). Failing loud + asking the caller
// to retry is strictly safer than a connection on the wrong branch.
async function fetchSignedUrl(apiBase: string, venueId: string): Promise<string | null> {
  for (let attempt = 1; attempt <= SIGNED_URL_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(`${apiBase}/venues/widget/signed-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venueId }),
      });
      if (response.ok) {
        const data = (await response.json()) as { signedUrl?: string };
        if (data.signedUrl) return data.signedUrl;
        console.warn(`catering-widget: signed-url attempt ${attempt} returned 200 with no URL`);
      } else {
        console.warn(`catering-widget: signed-url attempt ${attempt} failed (${response.status})`);
      }
    } catch (err) {
      console.warn(`catering-widget: signed-url attempt ${attempt} threw`, err);
    }
    if (attempt < SIGNED_URL_MAX_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, 400 * attempt)); // 400ms, 800ms backoff
    }
  }
  console.error(
    'catering-widget: signed-url failed after all attempts — failing loud, NOT falling back to the Main branch (would route the booking to the wrong environment)',
  );
  return null;
}

// Cached AudioContext shared across all `primeAudioContext()` calls in a
// session. Holding a module-level singleton is deliberate — the function is
// called on startCall, mode-switch, and every visibilitychange (return-from-
// background). Creating a new context each time would leak one AudioContext
// per screen-lock/unlock cycle on iOS. Subsequent calls just resume() if the
// cached context has been suspended by the OS.
let cachedAudioCtx: AudioContext | null = null;

// Pre-warm the browser AudioContext synchronously inside a click handler so it
// leaves the 'suspended' state BEFORE the ElevenLabs SDK starts streaming TTS.
// Without this, the first 1-2 seconds of the welcome message can clip because
// the SDK creates+resumes its own context AFTER the WebSocket is established.
// The first call plays a 50ms silent buffer to force the audio graph to be
// ready. Subsequent calls — e.g. from the visibilitychange listener after the
// user returns from the lock screen — just re-resume the cached context.
function primeAudioContext(): void {
  if (typeof window === 'undefined') return;
  try {
    const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    if (!cachedAudioCtx) {
      cachedAudioCtx = new AudioCtor();
      // First-call warmup — play a 50ms silent buffer to force the audio
      // graph up. Skipped on re-primes because the buffer is a one-shot.
      const buf = cachedAudioCtx.createBuffer(
        1,
        Math.floor(cachedAudioCtx.sampleRate * 0.05),
        cachedAudioCtx.sampleRate,
      );
      const src = cachedAudioCtx.createBufferSource();
      src.buffer = buf;
      src.connect(cachedAudioCtx.destination);
      src.start();
    }
    if (cachedAudioCtx.state === 'suspended') {
      void cachedAudioCtx.resume();
    }
  } catch (err) {
    console.warn('catering-widget: AudioContext pre-warm failed', err);
  }
}


function PulseDot({ color, inactiveColor, active }: { color: string; inactiveColor: string; active: boolean }) {
  return (
    <>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: active ? color : inactiveColor,
          display: 'inline-block',
          animation: active ? 'catering-pulse 1.4s ease-in-out infinite' : 'none',
        }}
      />
      <style>{`
        @keyframes catering-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
        /* Mobile spec from design review — keeps widget usable when the on-screen
           keyboard eats half the viewport. Shrinks logo + transcript minimums. */
        @media (max-width: 479px) {
          .catering-widget-idle { padding: 24px 16px 20px !important; }
          .catering-widget-idle > div:first-child {
            width: 80px !important;
            height: 80px !important;
            margin: 0 auto 14px !important;
          }
          .catering-widget-idle > div:first-child img {
            width: 80px !important;
            height: 80px !important;
          }
          .catering-widget-heading {
            font-size: clamp(18px, 4vw, 22px) !important;
          }
        }
      `}</style>
    </>
  );
}
